import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../../../../services/axiosClient';
import Swal from 'sweetalert2';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import LinkTool from '@editorjs/link';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import './EntrepriseDashboard.css';
import TarifEntreprise from './TarifEntreprise';
import MessagesPage from '../../../../../components/consultant/MessagesPage';
import ImpersonationExitButton from '../../../../../components/ImpersonationExitButton';
import API_CONFIG from '../../../../../shared/config/api';

const senoffreLogo = "https://res.cloudinary.com/dihtuaq6d/image/upload/v1760187144/xrk9fhrhbahbkvjzy1db.jpg";

// Utility function to fix HTTPS to HTTP for localhost URLs
const fixLocalhostUrl = (url) => {
    if (!url) return url;
    if (typeof url === 'string' && url.startsWith('https://localhost')) {
        return url.replace('https://localhost', 'http://localhost');
    }
    if (typeof url === 'object') {
        const fixed = {};
        Object.keys(url).forEach(key => {
            fixed[key] = fixLocalhostUrl(url[key]);
        });
        return fixed;
    }
    return url;
};

const EntrepriseDashboard = () => {
    const [activeTab, setActiveTab] = useState('parcourir-offres');
    const [userProfile, setUserProfile] = useState({
        name: 'Block Général',
        email: 'block.general@senoffre.com',
        avatar: null,
        lastLogin: '2025-09-28',
        isVerified: true
    });
    const [showDropdown, setShowDropdown] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    // Filter values sent to API (IDs)
    const [filters, setFilters] = useState({
        sector_id: '',
        city_id: '',
        post_type_id: ''
    });
    // Labels displayed in UI chips
    const [filterLabels, setFilterLabels] = useState({ sector: '', city: '', type: '' });
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilterType, setActiveFilterType] = useState(null);
    const [savedPostIds, setSavedPostIds] = useState([]); // IDs des posts sauvegardés
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 12,
        hasNextPage: false,
        hasPrevPage: false
    });
    
    // State for companies
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [companiesError, setCompaniesError] = useState('');
    const [companiesPagination, setCompaniesPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false
    });

    // My Posts (Mes annonces)
    const [myPosts, setMyPosts] = useState([]);
    const [myPostsLoading, setMyPostsLoading] = useState(false);
    const [myPostsError, setMyPostsError] = useState('');
    const [myPostsPagination, setMyPostsPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false,
    });
    const [myPostsSearchQuery, setMyPostsSearchQuery] = useState('');
    const [selectedPosts, setSelectedPosts] = useState([]); // ids

    // Pending Posts (En attente)
    const [pendingPosts, setPendingPosts] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [pendingError, setPendingError] = useState('');
    const [pendingPagination, setPendingPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false,
    });
    const [pendingSearchQuery, setPendingSearchQuery] = useState('');
    const [selectedPending, setSelectedPending] = useState([]); // ids

    // Archived Posts (Mes archives)
    const [archivedPosts, setArchivedPosts] = useState([]);
    const [archivedLoading, setArchivedLoading] = useState(false);
    const [archivedError, setArchivedError] = useState('');
    const [archivedPagination, setArchivedPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false,
    });
    const [archivedSearchQuery, setArchivedSearchQuery] = useState('');
    const [selectedArchived, setSelectedArchived] = useState([]); // ids

    // Publish permission via subscription status
    const [publishAllowed, setPublishAllowed] = useState(false);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');

    // Create Post modal & form state
    const [showPostModal, setShowPostModal] = useState(false);
    const [postSubmitting, setPostSubmitting] = useState(false);
    const [postError, setPostError] = useState('');
    const [postSuccess, setPostSuccess] = useState('');
    const [editingPostId, setEditingPostId] = useState(null);
    const [postForm, setPostForm] = useState({
        // Payload fields
        country_code: 'SN',
        title: '',
        description: '',
        category_id: '',
        post_type_id: '',
        city_id: '',
        contact_name: '',
        email: '',
        accept_terms: false,
        package_id: '',
        tags: '',
        company_name: '',
        company_description: '',
        
        // Additional UI fields
        selected_company_id: '',
        startup_date: '',
        phone_country: '+221',
        phone: '',
        hide_phone: false,
        application_url: ''
    });

    // API data states
    const [postTypes, setPostTypes] = useState([]);
    const [postTypesLoading, setPostTypesLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // Editor.js reference
    const editorRef = useRef(null);
    const editorInstance = useRef(null);

    // Initialize Editor.js
    const initializeEditor = () => {
        // Check if container exists
        const container = document.getElementById('editorjs-container');
        if (!container) {
            console.error('Editor container not found');
            return;
        }

        if (editorInstance.current) {
            editorInstance.current.destroy();
        }

        try {
            editorInstance.current = new EditorJS({
                holder: 'editorjs-container',
                placeholder: 'Cliquez ici pour commencer à écrire votre description...',
                autofocus: true,
                tools: {
                    header: Header,
                    list: List,
                    paragraph: {
                        class: Paragraph,
                        inlineToolbar: true
                    }
                },
                data: {
                    blocks: [
                        {
                            type: 'paragraph',
                            data: {
                                text: ''
                            }
                        }
                    ]
                },
                onChange: async () => {
                    try {
                        const outputData = await editorInstance.current.save();
                        setPostForm(p => ({ ...p, description: JSON.stringify(outputData) }));
                    } catch (e) {
                        console.error('Editor save error:', e);
                    }
                }
            });
            console.log('Editor.js initialized successfully');
            
            // Add a visual indicator that editor is ready
            setTimeout(() => {
                const editorContainer = document.getElementById('editorjs-container');
                if (editorContainer && !editorContainer.querySelector('.ce-paragraph')) {
                    // If no paragraph block exists, click to create one
                    editorContainer.click();
                }
            }, 100);
        } catch (error) {
            console.error('Failed to initialize Editor.js:', error);
            // Show fallback textarea
            const fallback = document.getElementById('description-fallback');
            const editorContainer = document.getElementById('editorjs-container');
            if (fallback && editorContainer) {
                editorContainer.style.display = 'none';
                fallback.style.display = 'block';
            }
        }
    };

    // Get editor data
    const getEditorData = async () => {
        if (editorInstance.current) {
            try {
                const outputData = await editorInstance.current.save();
                return JSON.stringify(outputData);
            } catch (e) {
                console.error('Editor get data error:', e);
                return '';
            }
        }
        return '';
    };

    // Create company modal & form state
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [companyForm, setCompanyForm] = useState({
        country_code: 'SN',
        name: '',
        description: '',
        city_id: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        facebook: '',
        twitter: '',
        linkedin: '',
        logoFile: null,
    });
    const [companyCreateLoading, setCompanyCreateLoading] = useState(false);
    const [companyCreateError, setCompanyCreateError] = useState('');
    const [editingCompanyId, setEditingCompanyId] = useState(null);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [selectedCompanies, setSelectedCompanies] = useState([]); // ids
    // Account (Mon compte)
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountError, setAccountError] = useState('');
    const [account, setAccount] = useState({
        civility: '',
        name: '',
        username: '',
        email: '',
        phone: '',
        phone_country: 'SN',
        photo_url: ''
    });
    const [maskPhone, setMaskPhone] = useState(false);
    const [accountSuccess, setAccountSuccess] = useState('');
    const [settingsSuccess, setSettingsSuccess] = useState('');
    // Settings (Paramètres)
    const [settings, setSettings] = useState({
        password: '',
        password_confirmation: '',
        marketing_opt_in: false,
        timezone: ''
    });
    // Photo upload
    const fileInputRef = useRef(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [photoSuccess, setPhotoSuccess] = useState('');
    // Fonction utilitaire pour l'authentification
    const getAuthHeader = () => {
        try {
            const str = localStorage.getItem('userDetails');
            if (!str) return {};
            const parsed = JSON.parse(str);
            const token = parsed?.token || parsed?.access_token;
            return token ? { Authorization: `Bearer ${token}` } : {};
        } catch (e) {
            console.error('Auth header error:', e);
            return {};
        }
    };

    // Handle authentication errors
    const handleAuthError = (error) => {
        const message = error?.response?.data?.message || error?.response?.data?.error || '';
        if (message.includes('unauthenticated') || message.includes('token_expired') || error?.response?.status === 401) {
            localStorage.removeItem('userDetails');
            window.location.href = '/login';
            return true;
        }
        return false;
    };

    // Check if current user is allowed to publish based on subscription status
    const checkSubscriptionStatus = async () => {
        setSubscriptionLoading(true);
        setSubscriptionError('');
        try {
            const res = await axiosClient.get('/users/subscription/status', {
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() },
            });
            const root = res?.data || {};
            const result = root?.result || root?.data || {};
            const hasActive = !!result?.has_active_subscription;
            const canPublish = !!result?.can_publish;
            setPublishAllowed(hasActive && canPublish);
        } catch (e) {
            // On error, be safe and disallow publishing
            setPublishAllowed(false);
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            setSubscriptionError(String(msg));
        } finally {
            setSubscriptionLoading(false);
        }
    };

    useEffect(() => {
        checkSubscriptionStatus();
    }, []);

    // Logout with confirmation
    const confirmLogout = async () => {
        const res = await Swal.fire({
            title: 'Se déconnecter ?',
            text: 'Vous allez être déconnecté de votre session.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, déconnecter',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#d33',
        });
        if (res.isConfirmed) {
            try {
                const storages = [window.localStorage, window.sessionStorage];
                const keys = ['authData','auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
                for (const storage of storages) {
                    for (const key of keys) {
                        try { storage.removeItem(key); } catch (_) {}
                    }
                }
            } finally {
                window.location.href = '/login';
            }
        }
    };

    // Update only profile identity fields
    const updateProfile = async () => {
        const userId = getCurrentUserId();
        if (!userId) return;
        setAccountLoading(true);
        setAccountError('');
        setAccountSuccess('');
        try {
            await axiosClient.put(`/users/${userId}`, {
                name: account.name || '',
                email: account.email || '',
                phone: account.phone || '',
                ...(account.phone ? { phone_country: account.phone_country || 'SN' } : {}),
            }, {
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
            });
            setAccountSuccess('Profil mis à jour avec succès.');
            await fetchUserProfile();
        } catch (e) {
            console.error('Erreur mise à jour profil:', e);
            const status = e?.response?.status;
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            // Fallback: some APIs require POST + _method=PUT
            if (status === 405 || /bad request method/i.test(String(msg))) {
                try {
                    await axiosClient.post(`/users/${userId}`, {
                        name: account.name || '',
                        email: account.email || '',
                        phone: account.phone || '',
                        ...(account.phone ? { phone_country: account.phone_country || 'SN' } : {}),
                        _method: 'PUT',
                    }, {
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
                    });
                    setAccountSuccess('Profil mis à jour avec succès.');
                    await fetchUserProfile();
                    return;
                } catch (e2) {
                    const msg2 = e2?.response?.data?.message || e2?.message || 'Erreur réseau';
                    setAccountError(`Échec de la mise à jour: ${msg2}`);
                    return;
                }
            }
            setAccountError(`Échec de la mise à jour: ${msg}`);
        } finally {
            setAccountLoading(false);
        }
    };

    // Update settings (password, marketing, timezone)
    const updateSettings = async () => {
        const userId = getCurrentUserId();
        if (!userId) return;
        setSettingsSuccess('');
        setAccountError('');
        // Validate passwords if provided
        if ((settings.password || settings.password_confirmation) && settings.password !== settings.password_confirmation) {
            setAccountError('Les mots de passe ne correspondent pas.');
            return;
        }
        const payload = {
            // Only send password fields if non-empty
            ...(settings.password ? { password: settings.password } : {}),
            ...(settings.password_confirmation ? { password_confirmation: settings.password_confirmation } : {}),
            marketing_opt_in: settings.marketing_opt_in ? 1 : 0,
            accept_marketing_offers: settings.marketing_opt_in ? 1 : 0,
            timezone: settings.timezone || 'Africa/Dakar',
            time_zone: settings.timezone || 'Africa/Dakar',
        };
        try {
            await axiosClient.put(`/users/${userId}`, payload, {
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
            });
            setSettingsSuccess('Paramètres mis à jour avec succès.');
            // Clear password fields
            setSettings(s => ({ ...s, password: '', password_confirmation: '' }));
        } catch (e) {
            console.error('Erreur mise à jour paramètres:', e);
            const status = e?.response?.status;
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            if (status === 405 || /bad request method/i.test(String(msg))) {
                try {
                    await axiosClient.post(`/users/${userId}`, { ...payload, _method: 'PUT' }, {
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
                    });
                    setSettingsSuccess('Paramètres mis à jour avec succès.');
                    setSettings(s => ({ ...s, password: '', password_confirmation: '' }));
                    return;
                } catch (e2) {
                    const msg2 = e2?.response?.data?.message || e2?.message || 'Erreur réseau';
                    setAccountError(`Échec de la mise à jour des paramètres: ${msg2}`);
                    return;
                }
            }
            setAccountError(`Échec de la mise à jour des paramètres: ${msg}`);
        }
    };

    // Photo upload handlers
    const resolvePhotoUrl = (photoUrl, photoPath) => {
        const originHttp = 'https://update.senoffre.com';
        if (photoUrl && typeof photoUrl === 'string') {
            // Normalize localhost protocol issues
            return photoUrl.replace('https://localhost:8000', originHttp).replace('http://127.0.0.1:8000', originHttp);
        }
        if (photoPath && typeof photoPath === 'string') {
            const path = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
            return `${originHttp}/storage${path.includes('/storage/') ? path.replace('/storage', '') : path}`;
        }
        return '';
    };
    const handlePickPhoto = () => {
        try {
            if (fileInputRef && fileInputRef.current) {
                fileInputRef.current.click();
            }
        } catch (_) {}
    };

    const handlePhotoChange = async (e) => {
        const file = e?.target?.files && e.target.files[0];
        if (!file) {
            setPhotoError('Aucun fichier sélectionné.');
            return;
        }
        await uploadProfilePhoto(file);
        // reset input to allow same file selection again
        try { e.target.value = ''; } catch (_) {}
    };

    const uploadProfilePhoto = async (file) => {
        const userId = getCurrentUserId();
        if (!userId) {
            setPhotoError("Utilisateur introuvable.");
            return;
        }
        setPhotoUploading(true);
        setPhotoError('');
        setPhotoSuccess('');
        try {
            const fd = new FormData();
            fd.append('photo', file);
            await axiosClient.post(`/users/${userId}/photo`, fd, {
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() },
            });
            setPhotoSuccess('Photo mise à jour avec succès.');
            await fetchUserProfile();
        } catch (e) {
            const status = e?.response?.status;
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            // Retry with PUT or method override if API complains about method
            if (status === 405 || /bad request method/i.test(String(msg))) {
                try {
                    // Try PUT multipart
                    const fd2 = new FormData();
                    fd2.append('photo', file);
                    await axiosClient.put(`/users/${userId}/photo`, fd2, {
                        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() },
                    });
                    setPhotoSuccess('Photo mise à jour avec succès.');
                    await fetchUserProfile();
                    return;
                } catch (e2) {
                    try {
                        // Try POST with _method=PUT
                        const fd3 = new FormData();
                        fd3.append('photo', file);
                        fd3.append('_method', 'PUT');
                        await axiosClient.post(`/users/${userId}/photo`, fd3, {
                            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() },
                        });
                        setPhotoSuccess('Photo mise à jour avec succès.');
                        await fetchUserProfile();
                        return;
                    } catch (e3) {
                        const msg3 = e3?.response?.data?.message || e3?.message || 'Erreur réseau';
                        setPhotoError(`Échec du téléversement: ${msg3}`);
                        return;
                    }
                }
            }
            setPhotoError(`Échec du téléversement: ${msg}`);
        } finally {
            setPhotoUploading(false);
        }
    };

    // Helper: get current logged user id from storages
    const getCurrentUserId = () => {
        try {
            const storages = [window.localStorage, window.sessionStorage];
            const keys = ['authData','auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
            for (const storage of storages) {
                for (const key of keys) {
                    const raw = storage.getItem(key);
                    if (!raw) continue;
                    try {
                        const data = JSON.parse(raw);
                        if (data?.user?.id) return data.user.id;
                        if (data?.id) return data.id;
                        if (data?.userId) return data.userId;
                    } catch {}
                }
            }
        } catch {}
        return null;
    };

    // Fetch user profile for 'Mon compte'
    const fetchUserProfile = async () => {
        const userId = getCurrentUserId();
        if (!userId) {
            setAccountError("Utilisateur introuvable dans le stockage local.");
            return;
        }
        setAccountLoading(true);
        setAccountError('');
        try {
            const res = await axiosClient.get(`/users/${userId}`, {
                headers: { Accept: 'application/json', ...getAuthHeader() },
            });
            const payload = res?.data?.result || res?.data?.data || res?.data || {};
            setAccount({
                civility: payload.civility || payload.gender || '',
                name: payload.name || '',
                username: payload.username || payload.login || '',
                email: payload.email || '',
                phone: payload.phone || payload.phone_national || '',
                phone_country: payload.phone_country || payload.phone_country_code || 'SN',
                photo_url: resolvePhotoUrl(payload.photo_url, payload.photo),
            });
            
            // Formater la date de dernière connexion
            let lastLoginFormatted = '-';
            const lastLoginDate = payload.last_activity || payload.last_login_at || payload.last_login || payload.updated_at;
            if (lastLoginDate) {
                try {
                    const date = new Date(lastLoginDate);
                    lastLoginFormatted = date.toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (e) {
                    lastLoginFormatted = lastLoginDate;
                }
            }
            
            // Sync header name and lastLogin
            setUserProfile(prev => ({ 
                ...prev, 
                name: payload.name || prev.name,
                lastLogin: lastLoginFormatted
            }));
            // Pre-fill settings if API returns preferences
            setSettings(s => ({
                ...s,
                marketing_opt_in: !!(payload.marketing_opt_in || payload.accept_marketing || payload.accept_marketing_offers || false),
                timezone: payload.timezone || payload.preferred_timezone || payload.time_zone || s.timezone || 'Africa/Dakar'
            }));
        } catch (e) {
            console.error('Erreur chargement profil:', e);
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            setAccountError(`Impossible de charger le profil: ${msg}`);
        } finally {
            setAccountLoading(false);
        }
    };

    // Fetch user's archived posts (archived=1)
    const fetchArchivedPosts = async (page = 1) => {
        setArchivedLoading(true);
        setArchivedError('');
        try {
            const response = await axiosClient.get('/posts', {
                params: {
                    belongLoggedUser: 1,
                    archived: 1,
                    perPage: archivedPagination.itemsPerPage,
                    page,
                },
                headers: { Accept: 'application/json', ...getAuthHeader() },
            });

            const root = response?.data || {};
            const result = root?.result || root;
            let items = result?.data || root?.data || [];
            if (!Array.isArray(items)) items = Array.isArray(items?.data) ? items.data : [];

            const mapped = items.map((it) => {
                const company = it.company || {};
                const category = it.category || it.categories?.[0] || {};
                // Utiliser logo_url direct de l'objet post (selon la réponse API)
                const logoUrl = (it.logo_url && (it.logo_url.small || it.logo_url.medium || it.logo_url.large || it.logo_url.full)) || 
                                (company?.logo_url && (company?.logo_url.small || company?.logo_url.full)) || 
                                company?.logo || 
                                null;
                return {
                    id: it.id,
                    title: it.title || it.name || '—',
                    companyName: company?.name || it.company_name || '—',
                    logo: logoUrl,
                    salary: it.salary_formatted || it.salary || '—',
                    created_at: it.created_at || it.created_at_formatted || null,
                    category: category?.name || it.category_name || '',
                    status: 'ARCHIVÉ',
                    // Conserver les données complètes pour l'édition
                    category_id: it.category_id || category?.id,
                    post_type_id: it.post_type_id,
                    city_id: it.city_id,
                    contact_name: it.contact_name,
                    email: it.email,
                    phone: it.phone,
                    phone_country: it.phone_country,
                    hide_phone: it.hide_phone,
                    application_url: it.application_url,
                    description: it.description,
                    tags: it.tags,
                    package_id: it.package_id,
                    startup_date: it.startup_date,
                    company_id: it.company_id,
                    country_code: it.country_code,
                    // Conserver les données brutes complètes
                    _rawData: it
                };
            });

            const meta = result?.meta || root?.meta || {};
            const current_page = meta.current_page || meta.currentPage || 1;
            const last_page = meta.last_page || meta.lastPage || 1;
            const total = meta.total || mapped.length;
            const per_page = meta.per_page || archivedPagination.itemsPerPage;

            setArchivedPosts(mapped);
            setArchivedPagination({
                currentPage: current_page,
                totalPages: last_page,
                totalItems: total,
                itemsPerPage: per_page,
                hasNextPage: current_page < last_page,
                hasPrevPage: current_page > 1,
            });
        } catch (err) {
            console.error('Erreur lors du chargement des archives:', err);
            setArchivedError('Erreur lors du chargement de vos archives. Veuillez réessayer.');
            setArchivedPosts([]);
        } finally {
            setArchivedLoading(false);
        }
    };

    // Fetch user's pending posts (pendingApproval=1)
    const fetchPendingPosts = async (page = 1) => {
        setPendingLoading(true);
        setPendingError('');
        try {
            const response = await axiosClient.get('/posts', {
                params: {
                    belongLoggedUser: 1,
                    pendingApproval: 1,
                    perPage: pendingPagination.itemsPerPage,
                    page,
                },
                headers: { Accept: 'application/json', ...getAuthHeader() },
            });

            const root = response?.data || {};
            const result = root?.result || root;
            let items = result?.data || root?.data || [];
            if (!Array.isArray(items)) items = Array.isArray(items?.data) ? items.data : [];

        const mapped = items.map((it) => {
            const company = it.company || {};
            const category = it.category || it.categories?.[0] || {};
            // Utiliser logo_url direct de l'objet post (selon la réponse API)
            const logoUrl = (it.logo_url && (it.logo_url.small || it.logo_url.medium || it.logo_url.large || it.logo_url.full)) || 
                            (company?.logo_url && (company?.logo_url.small || company?.logo_url.full)) || 
                            company?.logo || 
                            null;
            return {
                id: it.id,
                title: it.title || it.name || '—',
                companyName: company?.name || it.company_name || '—',
                logo: logoUrl,
                salary: it.salary_formatted || it.salary || '—',
                created_at: it.created_at || it.created_at_formatted || null,
                category: category?.name || it.category_name || '',
                status: 'EN ATTENTE',
                // Conserver les données complètes pour l'édition
                category_id: it.category_id || category?.id,
                post_type_id: it.post_type_id,
                city_id: it.city_id,
                contact_name: it.contact_name,
                email: it.email,
                phone: it.phone,
                phone_country: it.phone_country,
                hide_phone: it.hide_phone,
                application_url: it.application_url,
                description: it.description,
                tags: it.tags,
                package_id: it.package_id,
                startup_date: it.startup_date,
                company_id: it.company_id,
                country_code: it.country_code,
                // Conserver les données brutes complètes
                _rawData: it
            };
        });
            const meta = result?.meta || {};
            const current_page = meta.current_page || meta.currentPage || 1;
            const last_page = meta.last_page || meta.lastPage || 1;
            const total = meta.total || mapped.length;
            const per_page = meta.per_page || pendingPagination.itemsPerPage;

            setPendingPosts(mapped);
            setPendingPagination({
                currentPage: current_page,
                totalPages: last_page,
                totalItems: total,
                itemsPerPage: per_page,
                hasNextPage: current_page < last_page,
                hasPrevPage: current_page > 1,
            });
        } catch (err) {
            console.error('Erreur lors du chargement des annonces en attente:', err);
            setPendingError('Erreur lors du chargement des annonces en attente. Veuillez réessayer.');
            setPendingPosts([]);
        } finally {
            setPendingLoading(false);
        }
    };

    

    // Create a new company
    const createCompany = async () => {
        if (!companyForm.name || !companyForm.description) {
            setCompanyCreateError('Veuillez remplir tous les champs obligatoires');
            return;
        }
        
        setCompanyCreateLoading(true);
        setCompanyCreateError('');
        try {
            const fd = new FormData();
            fd.append('company[country_code]', companyForm.country_code || 'SN');
            fd.append('company[name]', companyForm.name || '');
            fd.append('company[description]', companyForm.description || '');
            if (companyForm.city_id) fd.append('company[city_id]', companyForm.city_id);
            if (companyForm.address) fd.append('company[address]', companyForm.address);
            if (companyForm.phone) fd.append('company[phone]', companyForm.phone);
            if (companyForm.email) fd.append('company[email]', companyForm.email);
            if (companyForm.website) fd.append('company[website]', companyForm.website);
            if (companyForm.facebook) fd.append('company[facebook]', companyForm.facebook);
            if (companyForm.twitter) fd.append('company[twitter]', companyForm.twitter);
            if (companyForm.linkedin) fd.append('company[linkedin]', companyForm.linkedin);
            if (companyForm.logoFile) fd.append('company[logo]', companyForm.logoFile);

            const response = await axiosClient.post('/companies', fd, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Accept: 'application/json',
                    ...getAuthHeader(),
                },
            });

            // Fermer le modal immédiatement
            setShowCompanyModal(false);
            setCompanyCreateLoading(false);
            
            // Réinitialiser le formulaire
            setCompanyForm({
                country_code: 'SN',
                name: '',
                description: '',
                city_id: '',
                address: '',
                phone: '',
                email: '',
                website: '',
                facebook: '',
                twitter: '',
                linkedin: '',
                logoFile: null,
            });
            
            // Afficher le message de succès
            await Swal.fire({
                icon: 'success',
                title: 'Succès !',
                text: response.data?.message || 'Entreprise créée avec succès',
                confirmButtonColor: '#145650'
            });
            
            // Recharger la liste des entreprises
            fetchCompanies(1);
        } catch (err) {
            console.error('Erreur de création entreprise:', err);
            setCompanyCreateError("Impossible de créer l'entreprise. Vérifiez les champs et réessayez.");
            setCompanyCreateLoading(false);
        }
    };

    const openCompanyModal = () => {
        console.debug('[EntrepriseDashboard] openCompanyModal click');
        // reset editing mode and form
        setEditingCompanyId(null);
        setCompanyForm({
            country_code: 'SN',
            name: '',
            description: '',
            city_id: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            facebook: '',
            twitter: '',
            linkedin: '',
            logoFile: null,
        });
        setShowCompanyModal(true);
    };
    const closeCompanyModal = () => {
        console.debug('[EntrepriseDashboard] closeCompanyModal click');
        setShowCompanyModal(false);
        setCompanyCreateError('');
    };
    const handleCompanyInput = (field, value) => {
        setCompanyForm(prev => ({ ...prev, [field]: value }));
    };

    // Delete company with confirmation
    const deleteCompany = async (company) => {
        const result = await Swal.fire({
            title: 'Supprimer cette entreprise ?',
            text: `Voulez-vous vraiment supprimer "${company.name}" ? Cette action est irréversible.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                await axiosClient.delete(`/companies/${company.id}`, {
                    headers: {
                        Accept: 'application/json',
                        ...getAuthHeader(),
                    },
                });

                await Swal.fire({
                    icon: 'success',
                    title: 'Supprimée !',
                    text: 'L\'entreprise a été supprimée avec succès.',
                    confirmButtonColor: '#145650'
                });

                // Recharger la liste des entreprises
                fetchCompanies(companiesPagination.currentPage);
            } catch (err) {
                console.error('Erreur de suppression entreprise:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: err?.response?.data?.message || 'Impossible de supprimer l\'entreprise.',
                    confirmButtonColor: '#145650'
                });
            }
        }
    };

    // Delete post with confirmation
    const deletePost = async (post) => {
        const result = await Swal.fire({
            title: 'Supprimer cette annonce ?',
            text: `Voulez-vous vraiment supprimer "${post.title}" ? Cette action est irréversible.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                await axiosClient.delete(`/posts/${post.id}`, {
                    headers: {
                        Accept: 'application/json',
                        ...getAuthHeader(),
                    },
                });

                await Swal.fire({
                    icon: 'success',
                    title: 'Supprimée !',
                    text: 'L\'annonce a été supprimée avec succès.',
                    confirmButtonColor: '#145650'
                });

                // Recharger la liste appropriée selon l'onglet actif
                if (activeTab === 'mes-annonces') {
                    fetchMyPosts(myPostsPagination.currentPage);
                } else if (activeTab === 'en-attente') {
                    fetchPendingPosts(pendingPagination.currentPage);
                } else if (activeTab === 'mes-archives') {
                    fetchArchivedPosts(archivedPagination.currentPage);
                }
            } catch (err) {
                console.error('Erreur de suppression annonce:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: err?.response?.data?.message || 'Impossible de supprimer l\'annonce.',
                    confirmButtonColor: '#145650'
                });
            }
        }
    };

    // Edit post - open modal with post data
    const editPost = async (post) => {
        try {
            setEditingPostId(post.id);
            
            console.log('🔧 [DEBUG] Editing post:', post);
            
            // Use raw data if available, otherwise use mapped data
            const postData = post._rawData || post;
            
            console.log('🔧 [DEBUG] Using post data:', postData);
            console.log('🔧 [DEBUG] Company ID from API:', postData.company_id);
            console.log('🔧 [DEBUG] Selected company ID will be:', postData.company_id?.toString() || '');
            console.log('🔧 [DEBUG] Phone from API:', postData.phone);
            console.log('🔧 [DEBUG] Phone country from API:', postData.phone_country);
            
            // Fetch all companies from API to get the complete list
            try {
                console.log('🔧 [DEBUG] Fetching all companies from API: /companies');
                const companiesResponse = await axiosClient.get('/companies');
                console.log('🔧 [DEBUG] Full companies response:', companiesResponse);
                console.log('🔧 [DEBUG] Companies response data:', companiesResponse.data);
                
                // The correct structure is: response.data.result.data
                const allCompanies = companiesResponse.data?.result?.data || companiesResponse.data?.data || [];
                console.log('🔧 [DEBUG] Processed allCompanies:', allCompanies);
                console.log('🔧 [DEBUG] All companies from API:', allCompanies.map(c => ({ id: c.id, name: c.name })));
                
                // Update the companies list with fresh data
                setCompanies(allCompanies);
                console.log('🔧 [DEBUG] Updated companies state with', allCompanies.length, 'companies');
                
                // If we have a company_id, check if it exists in the current list
                if (postData.company_id) {
                    const companyExists = allCompanies.some(c => c.id == postData.company_id);
                    console.log('🔧 [DEBUG] Company', postData.company_id, 'exists in list:', companyExists);
                    
                    let finalSelectedCompanyId = postData.company_id?.toString() || '';
                    
                    if (!companyExists) {
                        // Try to find company by name as fallback
                        const targetCompanyName = postData.company_name || postData.company?.name;
                        console.log('🔧 [DEBUG] Looking for company by name:', targetCompanyName);
                        
                        if (targetCompanyName) {
                            const companyByName = allCompanies.find(c => 
                                c.name && c.name.toLowerCase().trim() === targetCompanyName.toLowerCase().trim()
                            );
                            
                            if (companyByName) {
                                console.log('🔧 [DEBUG] Found company by name:', companyByName);
                                // Use the ID of the found company for selection
                                finalSelectedCompanyId = companyByName.id.toString();
                                console.log('🔧 [DEBUG] Updated finalSelectedCompanyId to:', finalSelectedCompanyId);
                            } else {
                                console.log('🔧 [DEBUG] Company not found by name, fetching specific company:', `/companies/${postData.company_id}`);
                                // If not found by name, fetch the specific company
                                try {
                                    const companyResponse = await axiosClient.get(`/companies/${postData.company_id}`);
                                    console.log('🔧 [DEBUG] Full specific company response:', companyResponse);
                                    console.log('🔧 [DEBUG] Specific company response data:', companyResponse.data);
                                    
                                    // The correct structure for single company is: response.data.result
                                    const companyData = companyResponse.data?.result || companyResponse.data;
                                    
                                    console.log('🔧 [DEBUG] Specific company data from API:', companyData);
                                    
                                    if (companyData) {
                                        console.log('🔧 [DEBUG] Adding specific company to list');
                                        const newCompany = {
                                            id: companyData.id,
                                            name: companyData.name,
                                            description: companyData.description || ''
                                        };
                                        // Add company to the list
                                        setCompanies(prev => [...prev, newCompany]);
                                        console.log('🔧 [DEBUG] Added specific company from API:', newCompany);
                                        finalSelectedCompanyId = companyData.id.toString();
                                        console.log('🔧 [DEBUG] Updated finalSelectedCompanyId to fetched company:', finalSelectedCompanyId);
                                    }
                                } catch (error) {
                                    console.error('🔧 [DEBUG] Error fetching specific company:', error);
                                    // Fallback: use company data from post if API call fails
                                    if (postData.company_name) {
                                        console.log('🔧 [DEBUG] Using fallback company data from post');
                                        const newCompany = {
                                            id: postData.company_id,
                                            name: postData.company_name,
                                            description: postData.company_description || ''
                                        };
                                        setCompanies(prev => [...prev, newCompany]);
                                        console.log('🔧 [DEBUG] Added fallback company:', newCompany);
                                        finalSelectedCompanyId = postData.company_id.toString();
                                        console.log('🔧 [DEBUG] Updated finalSelectedCompanyId to fallback:', finalSelectedCompanyId);
                                    }
                                }
                            }
                        }
                    }
                    
                    // Store the final selected company ID for form initialization
                    postData._finalSelectedCompanyId = finalSelectedCompanyId;
                    console.log('🔧 [DEBUG] Final selected company ID determined:', finalSelectedCompanyId);
                }
            } catch (error) {
                console.error('🔧 [DEBUG] Error fetching companies:', error);
                // Fallback: use existing companies list
                console.log('🔧 [DEBUG] Using existing companies list as fallback');
            }
            
            // Pre-fill form with existing data AFTER company resolution
            const finalSelectedCompanyId = postData._finalSelectedCompanyId || postData.company_id?.toString() || '';
            console.log('🔧 [DEBUG] Final selected_company_id will be:', finalSelectedCompanyId);
            
            setPostForm({
                country_code: postData.country_code || 'SN',
                title: postData.title || '',
                description: postData.description || '',
                category_id: postData.category_id?.toString() || postData.category?.id?.toString() || '',
                post_type_id: postData.post_type_id?.toString() || '',
                city_id: postData.city_id?.toString() || postData.city?.id?.toString() || '',
                contact_name: postData.contact_name || '',
                email: postData.email || '',
                accept_terms: true,
                package_id: postData.package_id?.toString() || '',
                tags: Array.isArray(postData.tags) ? postData.tags.join(', ') : (postData.tags || ''),
                
                // Additional UI fields
                selected_company_id: finalSelectedCompanyId,
                startup_date: postData.start_date || '',
                phone_country: postData.phone_country || '+221',
                phone: postData.phone || '',
                hide_phone: !!postData.phone_hidden,
                application_url: postData.application_url || ''
            });
            
            console.log('🔧 [DEBUG] PostForm after update:', {
                selected_company_id: finalSelectedCompanyId
            });
            
            // Add a small delay to check the final state
            setTimeout(() => {
                console.log('🔧 [DEBUG] Companies state after update:', companies.map(c => ({ id: c.id, name: c.name })));
                console.log('🔧 [DEBUG] Final selected_company_id:', postData.company_id?.toString() || '');
                console.log('🔧 [DEBUG] Current postForm.selected_company_id:', postForm.selected_company_id);
            }, 1000);
            
            // Add another check after the modal opens
            setTimeout(() => {
                console.log('🔧 [DEBUG] PostForm after modal opens:', {
                    selected_company_id: postForm.selected_company_id,
                    target_id: postData.company_id?.toString() || ''
                });
            }, 2000);
            
            setShowPostModal(true);
            
            // Initialize editor with existing content after modal opens
            setTimeout(() => {
                initializeEditor();
                
                // Set content for both Editor.js and contentEditable fallback
                setTimeout(async () => {
                    if (postData.description) {
                        // Try to set Editor.js content
                        if (editorInstance.current) {
                            try {
                                const content = typeof postData.description === 'string' && postData.description.startsWith('{') 
                                    ? JSON.parse(postData.description) 
                                    : { blocks: [{ type: 'paragraph', data: { text: postData.description } }] };
                                await editorInstance.current.render(content);
                            } catch (e) {
                                console.error('Error setting editor content:', e);
                            }
                        }
                        
                        // Set contentEditable fallback content
                        const editorContent = document.querySelector('.editor-content');
                        if (editorContent) {
                            // If description is JSON, extract text
                            let displayText = postData.description;
                            if (typeof postData.description === 'string' && postData.description.startsWith('{')) {
                                try {
                                    const parsed = JSON.parse(postData.description);
                                    if (parsed.blocks && parsed.blocks.length > 0) {
                                        displayText = parsed.blocks.map(block => block.data?.text || '').join('\n');
                                    }
                                } catch (e) {
                                    console.error('Error parsing description JSON:', e);
                                }
                            }
                            editorContent.innerHTML = displayText;
                        }
                    }
                }, 500);
            }, 100);
            
        } catch (error) {
            console.error('Error setting up edit form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Impossible de préparer le formulaire d\'édition.',
                confirmButtonColor: '#145650'
            });
        }
    };

    // Selection helpers for companies table
    const isSelected = (id) => selectedCompanies.includes(id);
    const toggleSelectOne = (id) => {
        setSelectedCompanies((prev) => (
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        ));
    };
    const toggleSelectAll = () => {
        // Select/unselect all visible (filtered) companies (no user filter)
        const visible = (companies || [])
            .filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase()));
        const allIds = visible.map(c => c.id);
        const allSelected = allIds.every(id => selectedCompanies.includes(id)) && allIds.length > 0;
        setSelectedCompanies(allSelected ? selectedCompanies.filter(id => !allIds.includes(id)) : Array.from(new Set([...selectedCompanies, ...allIds])));
    };
    const clearSelection = () => setSelectedCompanies([]);

    // Selection helpers for posts table
    const isPostSelected = (id) => selectedPosts.includes(id);
    const toggleSelectPostOne = (id) => {
        setSelectedPosts((prev) => (
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        ));
    };
    const toggleSelectPostAll = () => {
        const visible = (myPosts || [])
            .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(myPostsSearchQuery.toLowerCase()));
        const allIds = visible.map(p => p.id);
        const allSelected = allIds.every(id => selectedPosts.includes(id)) && allIds.length > 0;
        setSelectedPosts(allSelected ? selectedPosts.filter(id => !allIds.includes(id)) : Array.from(new Set([...selectedPosts, ...allIds])));
    };
    const clearPostsSelection = () => setSelectedPosts([]);

    // Selection helpers for pending posts table
    const isPendingSelected = (id) => selectedPending.includes(id);
    const toggleSelectPendingOne = (id) => {
        setSelectedPending((prev) => (
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        ));
    };
    const toggleSelectPendingAll = () => {
        const visible = (pendingPosts || [])
            .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(pendingSearchQuery.toLowerCase()));
        const allIds = visible.map(p => p.id);
        const allSelected = allIds.every(id => selectedPending.includes(id)) && allIds.length > 0;
        setSelectedPending(allSelected ? selectedPending.filter(id => !allIds.includes(id)) : Array.from(new Set([...selectedPending, ...allIds])));
    };
    const clearPendingSelection = () => setSelectedPending([]);

    // Selection helpers for archived posts table
    const isArchivedSelected = (id) => selectedArchived.includes(id);
    const toggleSelectArchivedOne = (id) => {
        setSelectedArchived((prev) => (
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        ));
    };
    const toggleSelectArchivedAll = () => {
        const visible = (archivedPosts || [])
            .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(archivedSearchQuery.toLowerCase()));
        const allIds = visible.map(p => p.id);
        const allSelected = allIds.every(id => selectedArchived.includes(id)) && allIds.length > 0;
        setSelectedArchived(allSelected ? selectedArchived.filter(id => !allIds.includes(id)) : Array.from(new Set([...selectedArchived, ...allIds])));
    };
    const clearArchivedSelection = () => setSelectedArchived([]);

    // Open modal prefilled for edition
    const openEditCompanyModal = (company) => {
        console.debug('[openEditCompanyModal] company.id=', company?.id);
        setEditingCompanyId(company?.id || null);
        setCompanyForm({
            country_code: company?.country_code || 'SN',
            name: company?.name || '',
            description: company?.description || '',
            city_id: company?.city_id || '',
            address: company?.address || '',
            phone: company?.phone || '',
            email: company?.email || '',
            website: company?.website || '',
            logoFile: null,
        });
        setShowCompanyModal(true);
    };

    // Update existing company via multipart (method spoofing for broad compatibility)
    const updateCompany = async () => {
        if (!editingCompanyId) return;
        setCompanyCreateLoading(true);
        setCompanyCreateError('');
        try {
            const fd = new FormData();
            // Some APIs expect the id inside the payload as well
            fd.append('company[id]', String(editingCompanyId));
            if (companyForm.country_code) fd.append('company[country_code]', companyForm.country_code);
            // REQUIRED fields: always send (trimmed)
            fd.append('company[name]', (companyForm.name || '').trim());
            fd.append('company[description]', (companyForm.description || '').trim());
            if (companyForm.city_id) fd.append('company[city_id]', companyForm.city_id);
            if (companyForm.address) fd.append('company[address]', companyForm.address);
            if (companyForm.phone) fd.append('company[phone]', companyForm.phone);
            if (companyForm.email) fd.append('company[email]', companyForm.email);
            if (companyForm.website) fd.append('company[website]', companyForm.website);
            if (companyForm.logoFile) fd.append('company[logo]', companyForm.logoFile);
            // Laravel-friendly method spoofing to avoid PUT multipart edge-cases
            fd.append('_method', 'PUT');

            // Debug: inspect payload keys
            try {
                for (const [k, v] of fd.entries()) {
                    const val = v instanceof File ? `File(${v.name})` : (typeof v === 'string' ? `${v} (len:${v.length})` : v);
                    console.debug('[updateCompany] fd', k, val);
                }
            } catch {}

            console.debug('[updateCompany] targetId=', editingCompanyId);
            await axiosClient.post(`/companies/${editingCompanyId}`, fd, {
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    // Do NOT set Content-Type manually; let the browser add the correct boundary
                    ...getAuthHeader(),
                },
            });

            setShowCompanyModal(false);
            setEditingCompanyId(null);
            fetchCompanies(companiesPagination.currentPage || 1);
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message || err?.message;
            const fieldErrors = err?.response?.data?.errors;
            console.error('Erreur de mise à jour entreprise:', status, msg, err?.response?.data);
            if (status === 401 || status === 419) {
                setCompanyCreateError('Session expirée. Veuillez vous reconnecter.');
            } else if (status === 403) {
                setCompanyCreateError("Vous n'avez pas l'autorisation de modifier cette entreprise.");
            } else if (fieldErrors) {
                // Concatène les erreurs champ par champ pour visibilité dans la modale
                const flat = Object.values(fieldErrors).flat().join(' ');
                setCompanyCreateError(flat || msg || "Impossible de mettre à jour l'entreprise.");
            } else {
                setCompanyCreateError(msg || "Impossible de mettre à jour l'entreprise. Réessayez.");
            }
        } finally {
            setCompanyCreateLoading(false);
        }
    };

    // Fetch companies function
    const fetchCompanies = async (page = 1) => {
        setCompaniesLoading(true);
        setCompaniesError('');
        
        try {
            const response = await axiosClient.get(`/companies`, {
                params: {
                    belongLoggedUser: 1,
                    perPage: companiesPagination.itemsPerPage,
                    page: page,
                    countPosts: 1
                },
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeader()
                }
            });

            const root = response?.data || {};
            const result = root?.result || root; // support both {result:{data,...}} and {data,...}
            let items = result?.data || root?.data || [];
            if (!Array.isArray(items)) items = [];

            const list = Array.isArray(items) ? items : [];
            const mapped = list.map(it => {
                const logoUrl = (it.logo_url && (it.logo_url.small || it.logo_url.full)) || it.logo || null;
                
                return {
                    id: it.id,
                    user_id: it.user_id,
                    name: it.name,
                    description: it.description || '',
                    email: it.email || '',
                    phone: it.phone || '',
                    address: it.address || '',
                    country_code: it.country_code || '',
                    city_id: it.city_id,
                    posts_count: it.posts_count || 0,
                    // unify logo access for current UI which reads company.logo
                    logo: logoUrl,
                    logo_url: it.logo_url || null,
                    created_at: it.created_at || null,
                    status: 'active',
                };
            });

            // Pagination meta
            const meta = result?.meta || root?.meta || {};
            const current_page = meta.current_page || meta.currentPage || 1;
            const last_page = meta.last_page || meta.lastPage || 1;
            const total = meta.total || mapped.length;
            const per_page = meta.per_page || companiesPagination.itemsPerPage;

            setCompanies(mapped);
            setCompaniesPagination({
                currentPage: current_page,
                totalPages: last_page,
                totalItems: total,
                itemsPerPage: per_page,
                hasNextPage: current_page < last_page,
                hasPrevPage: current_page > 1,
            });
        } catch (err) {
            console.error('Companies API fetch error:', err);
            if (handleAuthError(err)) return; // Redirect to login if auth error
            setCompaniesError("Impossible de charger les entreprises. Vérifiez votre connexion.");
            setCompanies([]);
        } finally {
            setCompaniesLoading(false);
        }
    };

    // Fetch logged user's posts (Mes annonces)
    const fetchMyPosts = async (page = 1) => {
        setMyPostsLoading(true);
        setMyPostsError('');
        try {
            const response = await axiosClient.get('/posts', {
                params: {
                    belongLoggedUser: 1,
                    pendingApproval: 1,
                    perPage: myPostsPagination.itemsPerPage,
                    page,
                    embed: 'company,category',
                },
                headers: {
                    Accept: 'application/json',
                    ...getAuthHeader(),
                },
            });

            const root = response?.data || {};
            const result = root?.result || root;
            let items = result?.data || root?.data || [];
            if (!Array.isArray(items)) items = Array.isArray(items?.data) ? items.data : [];

            const mapped = items.map((it) => {
                const company = it.company || {};
                const category = it.category || it.categories?.[0] || {};
                // Utiliser logo_url direct de l'objet post (selon la réponse API)
                const logoUrl = (it.logo_url && (it.logo_url.small || it.logo_url.medium || it.logo_url.large || it.logo_url.full)) || 
                                (company?.logo_url && (company?.logo_url.small || company?.logo_url.full)) || 
                                company?.logo || 
                                null;
                return {
                    id: it.id,
                    title: it.title || it.name || '—',
                    companyName: company?.name || it.company_name || '—',
                    logo: logoUrl,
                    salary: it.salary_formatted || it.salary || '—',
                    created_at: it.created_at || it.created_at_formatted || null,
                    category: category?.name || it.category_name || '',
                    status: it.archived ? 'ARCHIVÉ' : 'ACTIF',
                    // Conserver les données complètes pour l'édition
                    category_id: it.category_id || category?.id,
                    post_type_id: it.post_type_id,
                    city_id: it.city_id,
                    contact_name: it.contact_name,
                    email: it.email,
                    phone: it.phone,
                    phone_country: it.phone_country,
                    hide_phone: it.hide_phone,
                    application_url: it.application_url,
                    description: it.description,
                    tags: it.tags,
                    package_id: it.package_id,
                    startup_date: it.startup_date,
                    company_id: it.company_id,
                    country_code: it.country_code,
                    // Conserver les données brutes complètes
                    _rawData: it
                };
            });

            const meta = result?.meta || root?.meta || {};
            const current_page = meta.current_page || meta.currentPage || 1;
            const last_page = meta.last_page || meta.lastPage || 1;
            const total = meta.total || mapped.length;
            const per_page = meta.per_page || myPostsPagination.itemsPerPage;

            setMyPosts(mapped);
            setMyPostsPagination({
                currentPage: current_page,
                totalPages: last_page,
                totalItems: total,
                itemsPerPage: per_page,
                hasNextPage: current_page < last_page,
                hasPrevPage: current_page > 1,
            });
        } catch (err) {
            console.error('Erreur lors du chargement de mes annonces:', err);
            setMyPostsError("Erreur lors du chargement de vos annonces. Veuillez réessayer.");
            setMyPosts([]);
        } finally {
            setMyPostsLoading(false);
        }
    };

    // Masquer les éléments de navigation par défaut
    useEffect(() => {
        document.body.classList.add('modern-dashboard');
        return () => {
            document.body.classList.remove('modern-dashboard');
        };
    }, []);

    // Fetch post types
    const fetchPostTypes = async () => {
        setPostTypesLoading(true);
        try {
            const response = await axiosClient.get('/postTypes', {
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeader() 
                },
            });
            
            if (response.status === 200) {
                const data = response.data;
                let typeList = data?.result?.data || data?.data || data?.result || data || [];
                if (!Array.isArray(typeList)) {
                    if (typeList && Array.isArray(typeList.data)) typeList = typeList.data;
                    else typeList = [];
                }
                
                const normalizedTypes = typeList.map(type => ({
                    id: type.id,
                    name: type.name || type.title || `Type #${type.id}`
                }));
                
                setPostTypes(normalizedTypes);
            }
        } catch (e) {
            console.error('PostTypes API fetch error:', e);
            if (handleAuthError(e)) return; // Redirect to login if auth error
            setPostTypes([
                { id: 1, name: 'Plein temps' },
                { id: 2, name: 'Temps partiel' },
                { id: 3, name: 'Contrat' },
                { id: 4, name: 'Freelance' }
            ]);
        } finally {
            setPostTypesLoading(false);
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const response = await axiosClient.get('/categories', {
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeader() 
                },
            });
            
            if (response.status === 200) {
                const data = response.data;
                let categoryList = data?.result?.data || data?.data || data?.result || data || [];
                if (!Array.isArray(categoryList)) {
                    if (categoryList && Array.isArray(categoryList.data)) categoryList = categoryList.data;
                    else categoryList = [];
                }
                
                const normalizedCategories = categoryList.map(category => {
                    let displayName = category.name || category.title || `Catégorie #${category.id}`;
                    // Gérer le cas où name est un objet (traductions)
                    if (displayName && typeof displayName === 'object') {
                        displayName = displayName.fr || displayName.en || displayName[Object.keys(displayName)[0]] || `Catégorie #${category.id}`;
                    }
                    return {
                        id: category.id,
                        name: String(displayName)
                    };
                });
                
                setCategories(normalizedCategories);
            }
        } catch (e) {
            console.error('Categories API fetch error:', e);
            if (handleAuthError(e)) return; // Redirect to login if auth error
            setCategories([
                { id: 1, name: 'Informatique' },
                { id: 2, name: 'Construction' },
                { id: 3, name: 'Commerce' }
            ]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Récupérer les villes du Sénégal
    const fetchCities = async () => {
        try {
            const response = await axiosClient.get('/countries/SN/cities?perPage=100', {
                headers: { 
                    'Accept': 'application/json',
                    ...getAuthHeader() 
                },
            });
            
            if (response.status === 200) {
                const data = response.data;
                let cityList = data?.result?.data || data?.data || data?.result || data || [];
                if (!Array.isArray(cityList)) {
                    if (cityList && Array.isArray(cityList.data)) cityList = cityList.data;
                    else cityList = [];
                }
                
                const normalizedCities = cityList.map(city => ({
                    id: city.id,
                    name: city.name,
                    country_code: city.country_code,
                    posts_count: city.posts_count || 0
                }));
                
                setCities(normalizedCities);
            }
        } catch (e) {
            console.error('Cities API fetch error:', e);
            // Fallback avec villes par défaut
            setCities([
                { id: 1, name: 'Dakar', country_code: 'SN', posts_count: 0 },
                { id: 2, name: 'Thiès', country_code: 'SN', posts_count: 0 },
                { id: 3, name: 'Saint-Louis', country_code: 'SN', posts_count: 0 },
                { id: 4, name: 'Kaolack', country_code: 'SN', posts_count: 0 },
                { id: 5, name: 'Ziguinchor', country_code: 'SN', posts_count: 0 },
                { id: 6, name: 'Tambacounda', country_code: 'SN', posts_count: 0 }
            ]);
        }
    };

    // Récupérer les posts sauvegardés
    const fetchSavedPosts = async () => {
        try {
            const response = await axiosClient.get('/savedPosts', {
                params: { country_code: 'SN', perPage: 100 },
                headers: { Accept: 'application/json', ...getAuthHeader() }
            });
            
            const data = response?.data;
            const savedPosts = data?.result?.data || data?.data || [];
            const postIds = savedPosts.map(item => item.post_id || item.id).filter(Boolean);
            setSavedPostIds(postIds);
        } catch (e) {
            console.error('Error fetching saved posts:', e);
            setSavedPostIds([]);
        }
    };

    // Sauvegarder ou retirer un post des favoris
    const toggleSavePost = async (postId) => {
        const isSaved = savedPostIds.includes(postId);
        
        try {
            if (isSaved) {
                // Retirer des favoris - DELETE /savedPosts/{post_id}
                await axiosClient.delete(`/savedPosts/${postId}`, {
                    headers: { Accept: 'application/json', ...getAuthHeader() }
                });
                setSavedPostIds(prev => prev.filter(id => id !== postId));
            } else {
                // Ajouter aux favoris - POST /savedPosts avec {"post_id": id}
                await axiosClient.post(`/savedPosts`, 
                    { post_id: postId },
                    { headers: { Accept: 'application/json', ...getAuthHeader() } }
                );
                setSavedPostIds(prev => [...prev, postId]);
            }
        } catch (e) {
            console.error('Error toggling save post:', e);
            const errorMsg = e?.response?.data?.message || e?.message;
            
            // Si l'erreur est 409 (déjà sauvegardé), on met à jour l'état quand même
            if (e?.response?.status === 409 && !isSaved) {
                setSavedPostIds(prev => [...prev, postId]);
            } else {
                alert(isSaved ? 'Erreur lors du retrait des favoris' : 'Erreur lors de la sauvegarde');
            }
        }
    };

    // Récupérer les Appels d'offres
    const fetchOffers = async (page = 1, customItemsPerPage = null) => {
        try {
            setLoading(true);
            setError('');
            const itemsPerPage = customItemsPerPage !== null ? customItemsPerPage : pagination.itemsPerPage;
            
            // Construire les paramètres selon la documentation API
            const params = { 
                page: page, 
                perPage: itemsPerPage
            };
            
            // Ajouter op=search si des filtres sont actifs ou si recherche globale
            const hasFilters = filters.sector_id || filters.city_id || filters.post_type_id || globalSearchQuery;
            if (hasFilters) {
                params.op = 'search';
            }
            
            // Ajouter la recherche globale
            if (globalSearchQuery && globalSearchQuery.trim()) {
                params.q = globalSearchQuery.trim();
            }
            
            // Ajouter les filtres avec les bons noms de paramètres
            if (filters.sector_id) params.c = filters.sector_id; // c pour category
            if (filters.city_id) params.l = filters.city_id; // l pour location (city ID)
            if (filters.post_type_id) params.type = filters.post_type_id; // type au lieu de post_type_id
            
            const response = await axiosClient.get('/posts', {
                params: params,
                headers: { Accept: 'application/json', ...getAuthHeader() },
            });
            
            const data = response?.data;
            console.log('API Response:', data);
            let items = data?.result?.data || data?.data || data?.result || data || [];
            if (!Array.isArray(items)) {
                if (items && Array.isArray(items.data)) items = items.data;
                else items = [];
            }
            
            // Normalisation des données
            const mapped = items.map((it) => {
                const cityData = cities.find(city => city.id === it.city_id) || cities[0];
                
                // Gérer le cas où category est un objet
                let categoryName = it.category_name || 'Services financiers';
                if (it.category) {
                    if (typeof it.category === 'object' && it.category.name) {
                        categoryName = it.category.name;
                    } else if (typeof it.category === 'string') {
                        categoryName = it.category;
                    }
                }
                
                // Gérer le cas où postType est un objet
                let typeName = 'CDI';
                if (it.postType) {
                    if (typeof it.postType === 'object' && it.postType.name) {
                        typeName = it.postType.name;
                    } else if (typeof it.postType === 'string') {
                        typeName = it.postType;
                    }
                } else if (it.type) {
                    typeName = it.type;
                } else if (it.post_type) {
                    typeName = it.post_type;
                }
                
                return {
                    id: it.id || it.uuid || it.slug || Math.random().toString(36).slice(2),
                    company: it.company_name || it.company || it.organisation || it.publisher || '—',
                    title: it.title || it.name || '—',
                    category: String(categoryName),
                    location: cityData?.name || it.city || it.location || 'Dakar',
                    type: String(typeName),
                    date: it.created_at_formatted || it.created_at || it.date || new Date().toLocaleDateString('fr-FR'),
                    description: it.description || it.excerpt || it.description_excerpt || '',
                    excerpt: it.excerpt || it.description_excerpt || '',
                    salary: it.salary_formatted || it.salary || 'FCFA',
                    logoUrl: it.logo_url, // Objet avec {small, medium, large, full}
                    visits: it.visits || it.visits_formatted || Math.floor(Math.random() * 1000),
                    reference: it.reference || it.id,
                    status: it.archived ? 'ARCHIVÉ' : 'ACTIF',
                    post_type_id: it.post_type_id,
                    featured: it.featured
                };
            });
            
            setOffers(mapped);
            
            // Mise à jour des informations de pagination
            const paginationInfo = data?.pagination || data?.meta || data?.result?.meta || {};
            console.log('Pagination Info:', paginationInfo);
            const currentPage = paginationInfo.current_page || page;
            const totalPages = paginationInfo.last_page || Math.ceil((paginationInfo.total || mapped.length) / itemsPerPage);
            const totalItems = paginationInfo.total || mapped.length;
            
            console.log('Calculated Pagination:', {
                currentPage,
                totalPages,
                totalItems,
                itemsPerPage,
                hasNextPage: currentPage < totalPages,
                hasPrevPage: currentPage > 1
            });
            
            setPagination({
                currentPage: currentPage,
                totalPages: totalPages,
                totalItems: totalItems,
                itemsPerPage: itemsPerPage,
                hasNextPage: currentPage < totalPages,
                hasPrevPage: currentPage > 1
            });
        } catch (e) {
            console.error('Fetch offers error:', e);
            const msg = e?.response?.data?.message || e?.message || 'Erreur réseau';
            setError(`Impossible de charger les offres: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCities();
        fetchSavedPosts(); // Charger les posts sauvegardés au démarrage
    }, []);

    useEffect(() => {
        if (cities.length > 0) {
            fetchOffers(1);
        }
    }, [cities]);

    // Fetch companies when the tab is active
    useEffect(() => {
        if (activeTab === 'mes-entreprises') {
            fetchCompanies(1);
        }
        if (activeTab === 'mes-annonces') {
            fetchMyPosts(1);
        }
        if (activeTab === 'en-attente') {
            fetchPendingPosts(1);
        }
        if (activeTab === 'mes-archives') {
            fetchArchivedPosts(1);
        }
        if (activeTab === 'mon-compte') {
            fetchUserProfile();
        }
    }, [activeTab]);

    // Load data when post modal opens
    useEffect(() => {
        if (showPostModal) {
            fetchPostTypes();
            fetchCategories();
            if (!cities || cities.length === 0) {
                fetchCities();
            }
            if (!companies || companies.length === 0) {
                fetchCompanies(1);
            }
            // Initialize Editor.js after modal is rendered
            setTimeout(() => {
                console.log('Attempting to initialize Editor.js...');
                initializeEditor();
            }, 500);
        } else {
            // Cleanup editor when modal closes
            if (editorInstance.current) {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
        }
    }, [showPostModal]);

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    // Load cities and categories from API
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await axiosClient.get('/countries/SN/cities?perPage=100', {
                    headers: { Accept: 'application/json', ...getAuthHeader() }
                });
                const root = res?.data || {};
                const items = root?.result?.data || root?.data || root?.result || [];
                const list = Array.isArray(items) ? items : Array.isArray(items?.data) ? items.data : [];
                setCities(list.map(c => ({ id: c.id, name: c.name || c.city || '-', posts_count: c.posts_count || 0 })));
            } catch (e) {
                console.error('Erreur chargement villes:', e);
            }
        };
        const fetchCategories = async () => {
            try {
                const res = await axiosClient.get('/categories', {
                    headers: { Accept: 'application/json', ...getAuthHeader() }
                });
                const root = res?.data || {};
                const items = root?.result?.data || root?.data || root?.result || [];
                const list = Array.isArray(items) ? items : Array.isArray(items?.data) ? items.data : [];
                // Normaliser les catégories et gérer les objets name
                const normalizedCategories = list.map(cat => {
                    let displayName = cat.name || cat.title || '-';
                    // Gérer le cas où name est un objet (traductions)
                    if (displayName && typeof displayName === 'object') {
                        displayName = displayName.fr || displayName.en || displayName[Object.keys(displayName)[0]] || '-';
                    }
                    return { 
                        id: cat.id, 
                        name: String(displayName)
                    };
                });
                setCategories(normalizedCategories);
            } catch (e) {
                console.error('Erreur chargement catégories:', e);
            }
        };
        const fetchPostTypes = async () => {
            try {
                const res = await axiosClient.get('/postTypes', {
                    headers: { Accept: 'application/json', ...getAuthHeader() }
                });
                const root = res?.data || {};
                const items = root?.result?.data || root?.data || root?.result || [];
                const list = Array.isArray(items) ? items : Array.isArray(items?.data) ? items.data : [];
                // Gérer les traductions comme dans JobTypes.js
                const normalizedTypes = list.map(pt => {
                    let displayName = pt.name || pt.title || pt.label;
                    if (displayName && typeof displayName === 'object') {
                        displayName = displayName.fr || displayName.en || displayName[Object.keys(displayName)[0]] || 'Sans nom';
                    }
                    return { 
                        id: pt.id || pt.post_type_id, 
                        name: String(displayName || 'Sans nom')
                    };
                });
                setPostTypes(normalizedTypes);
            } catch (e) {
                console.error('Erreur chargement types de contrat:', e);
            }
        };
        fetchCities();
        fetchCategories();
        fetchPostTypes();
    }, []);

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        fetchOffers(1);
    };
    
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: page }));
            fetchOffers(page);
            // Scroll to top of offers section
            document.querySelector('.offres-section')?.scrollIntoView({ behavior: 'smooth' });
        }
    };
    
    const handleItemsPerPageChange = (newItemsPerPage) => {
        setPagination(prev => ({ 
            ...prev, 
            itemsPerPage: newItemsPerPage, 
            currentPage: 1 
        }));
        fetchOffers(1, newItemsPerPage);
    };

    const menuItems = [
        { id: 'mes-entreprises', icon: 'fa-building', label: 'Mes entreprises', count: 1, active: false },
        { id: 'mes-annonces', icon: 'fa-list', label: 'Mes appels d\'offres ', active: false },
        { id: 'en-attente', icon: 'fa-hourglass-half', label: 'En attente', count: 1, active: false },
        { id: 'mes-archives', icon: 'fa-archive', label: 'Mes archives', active: false },
        { id: 'messenger', icon: 'fa-envelope', label: 'Messenger', active: false }
    ];

    const accountItems = [
        { id: 'mon-compte', icon: 'fa-user-cog', label: 'Mon compte', settings: true },
        { id: 'deconnexion', icon: 'fa-sign-out-alt', label: 'Déconnexion', emoji: '👤' },
        { id: 'supprimer-compte', icon: 'fa-trash', label: 'Supprimer le compte', danger: true }
    ];

    return (
      <div className={`modern-entreprise-dashboard ${sidebarOpen ? '' : 'collapsed'}`}>
          {/* Header */}
          <div className="dashboard-header">
              <div className="header-left">
                  <div className="logo-section" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/'}>
                        <img src={senoffreLogo} alt="SenOffre" className="logo" />
                  </div>
              </div>
                
                <div className="header-center">
                    <div className="search-bar">
                        <i className="fa fa-search search-icon"></i>
                        <input 
                            type="text" 
                            placeholder="Search here" 
                            value={globalSearchQuery}
                            onChange={(e) => setGlobalSearchQuery(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    fetchOffers(1);
                                }
                            }}
                        />
                    </div>
                </div>
                
                <div className="header-right">
                    <div className="header-actions">
                        {/* Bouton de sortie d'emprunt d'identité */}
                        <ImpersonationExitButton style={{ marginRight: '10px' }} />
                        
                        <button 
                            className={`action-btn parcourir-btn ${activeTab === 'parcourir-offres' ? 'active' : ''}`}
                            onClick={() => setActiveTab('parcourir-offres')}
                        >
                            <i className="fa fa-list"></i>
                            <span>Parcourir les offres</span>
                        </button>
                        
                        <button 
                            className={`action-btn entreprises-btn ${activeTab === 'mes-entreprises' ? 'active' : ''}`}
                            onClick={() => setActiveTab('mes-entreprises')}
                        >
                            <i className="fa fa-building"></i>
                            <span>Mes entreprises</span>
                        </button>
                        
                        {/* Removed Block Général button as requested */}
                        <button className="action-btn tarifs-btn" onClick={() => setActiveTab('tarif-entreprise')}>
                            <i className="fa fa-tag"></i>
                            <span>Tarifs</span>
                        </button>
                        
                        {(
                            <button className="action-btn publish-btn" onClick={() => {
                                setShowPostModal(true);
                            }}>
                                <span>Publier une Offre</span>
                            </button>
                        )}
                        
                        {/* Removed end notification icons as requested */}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="dashboard-content">
                <div className="content-wrapper">
                    {/* Left Sidebar */}
                    <div className="sidebar">
                        {/* <div className="profile-header">
                            <div className="profile-info">
                                <div className="profile-avatar">
                                    <i className="fa fa-user-circle"></i>
                                </div>
                                <h3 className="profile-name">{userProfile.name}</h3>
                                <p className="profile-role">Superadmin</p>
                                <div className="profile-dropdown">
                                    <i className="fa fa-chevron-down"></i>
                                </div>
                            </div>
                        </div> */}

                        <div className="sidebar-menu">
                            <div className="menu-section">
                                <div className="section-title-row">
                                    <h4 className="section-title">MES ANNONCES</h4>
                                </div>
                                {menuItems.map((item) => (
                                    <div key={item.id} className={`menu-item ${item.active ? 'active' : ''}`} onClick={() => setActiveTab(item.id)}>
                                        <i className={`fa ${item.icon}`}></i>
                                        <span>{item.label}</span>
                                        <i className="fa fa-chevron-right"></i>
                                    </div>
                                ))}
                            </div>

                            <div className="menu-section account-section">
                                <h4 className="section-title">MON COMPTE</h4>
                                {accountItems.map((item) => (
                                    <div key={item.id} className={`menu-item ${item.danger ? 'danger' : ''}`} onClick={() => {
                                        if (item.id === 'deconnexion') {
                                            confirmLogout();
                                        } else {
                                            setActiveTab(item.id);
                                        }
                                    }}>
                                        <i className={`fa ${item.icon}`}></i>
                                        <span>{item.label}</span>
                                        {item.settings && <span className="settings-icon">⚙️</span>}
                                        {item.emoji && <span className="emoji-icon">{item.emoji}</span>}
                                        <i className="fa fa-chevron-right"></i>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Main Dashboard Content */}
                    <div className="main-content">
                        {/* Floating Sidebar Toggle */}
                        <button 
                            className="sidebar-fab"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            aria-label={sidebarOpen ? 'Masquer le menu' : 'Afficher le menu'}
                            title={sidebarOpen ? 'Masquer le menu' : 'Afficher le menu'}
                        >
                            <i className="fa fa-bars"></i>
                        </button>
                        {activeTab === 'tarif-entreprise' ? (
                            <div className="offres-section">
                                <TarifEntreprise />
                            </div>
                        ) : activeTab === 'parcourir-offres' ? (
                            <div className="offres-section">
                                <div className="section-header">
                                    <h2>Parcourir les Offres</h2>
                                    <p>Découvrez tous les Appels d'offres disponibles</p>
                                </div>
                                
                                <div className="modern-filters-section">
                                    {/* Filter Toggle Button */}
                                    <div className="filter-header">
                                        <button 
                                            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <i className="fa fa-sliders-h"></i>
                                            <span>Filtres avancés</span>
                                            <i className={`fa fa-chevron-${showFilters ? 'up' : 'down'}`}></i>
                                        </button>
                                        
                                        {/* Active Filters Display */}
                                        <div className="active-filters">
                                            {filterLabels.sector && (
                                                <span className="filter-chip">
                                                    <i className="fa fa-briefcase"></i>
                                                    {filterLabels.sector}
                                                    <button onClick={() => { setFilters(prev => ({...prev, sector_id: ''})); setFilterLabels(prev => ({...prev, sector: ''})); }}>
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            )}
                                            {filterLabels.city && (
                                                <span className="filter-chip">
                                                    <i className="fa fa-map-marker-alt"></i>
                                                    {filterLabels.city}
                                                    <button onClick={() => { setFilters(prev => ({...prev, city_id: ''})); setFilterLabels(prev => ({...prev, city: ''})); }}>
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            )}
                                            {filterLabels.type && (
                                                <span className="filter-chip">
                                                    <i className="fa fa-clock"></i>
                                                    {filterLabels.type}
                                                    <button onClick={() => { setFilters(prev => ({...prev, post_type_id: ''})); setFilterLabels(prev => ({...prev, type: ''})); }}>
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                        
                                        <button 
                                            className="modern-search-btn"
                                            onClick={handleSearch}
                                            disabled={loading}
                                        >
                                            <i className="fa fa-search"></i>
                                            <span>{loading ? 'Recherche...' : 'Rechercher'}</span>
                                        </button>
                                    </div>
                                    
                                    {/* Expandable Filter Panel */}
                                    <div className={`filter-panel ${showFilters ? 'expanded' : ''}`}>
                                        <div className="filter-grid">
                                            {/* Secteur Filter */}
                                            <div className="filter-category">
                                                <h4 className="filter-title">
                                                    <i className="fa fa-briefcase"></i>
                                                    Secteur d'activité
                                                </h4>
                                                <select 
                                                    className="filter-select"
                                                    value={filters.sector_id || ''}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        const selectedCategory = categories.find(c => c.id == selectedId);
                                                        setFilters(prev => ({ ...prev, sector_id: selectedId }));
                                                        setFilterLabels(prev => ({ ...prev, sector: selectedId ? selectedCategory?.name || '' : '' }));
                                                    }}
                                                >
                                                    <option value="">Tous les secteurs</option>
                                                    {categories.map(s => (
                                                        <option key={s.id} value={s.id}>
                                                            {String(s.name).charAt(0).toUpperCase() + String(s.name).slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            {/* Ville Filter */}
                                            <div className="filter-category">
                                                <h4 className="filter-title">
                                                    <i className="fa fa-map-marker-alt"></i>
                                                    Localisation
                                                </h4>
                                                <select 
                                                    className="filter-select"
                                                    value={filters.city_id || ''}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        const selectedCity = cities.find(c => c.id == selectedId);
                                                        setFilters(prev => ({ ...prev, city_id: selectedId }));
                                                        setFilterLabels(prev => ({ ...prev, city: selectedId ? selectedCity?.name || '' : '' }));
                                                    }}
                                                >
                                                    <option value="">Toutes les villes</option>
                                                    {cities.map(city => (
                                                        <option key={city.id} value={city.id}>
                                                            {city.name} {city.posts_count > 0 && `(${city.posts_count})`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            {/* Type de contrat Filter */}
                                            <div className="filter-category">
                                                <h4 className="filter-title">
                                                    <i className="fa fa-clock"></i>
                                                    Type de contrat
                                                </h4>
                                                <select 
                                                    className="filter-select"
                                                    value={filters.post_type_id || ''}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        const selectedType = postTypes.find(t => t.id == selectedId);
                                                        setFilters(prev => ({ ...prev, post_type_id: selectedId }));
                                                        setFilterLabels(prev => ({ ...prev, type: selectedId ? selectedType?.name || '' : '' }));
                                                    }}
                                                >
                                                    <option value="">Tous les types</option>
                                                    {postTypes.map(t => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        
                                        {/* Filter Actions */}
                                        <div className="filter-actions">
                                            <button 
                                                className="clear-filters-btn"
                                                onClick={() => {
                                                    setFilters({ sector_id: '', city_id: '', post_type_id: '' });
                                                    setFilterLabels({ sector: '', city: '', type: '' });
                                                    handleSearch();
                                                }}
                                            >
                                                <i className="fa fa-times-circle"></i>
                                                Effacer tous les filtres
                                            </button>
                                            <button 
                                                className="apply-filters-btn"
                                                onClick={() => {
                                                    handleSearch();
                                                    setShowFilters(false);
                                                }}
                                            >
                                                <i className="fa fa-check"></i>
                                                Appliquer les filtres
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                {loading && (
                                    <div className="loading-section">
                                        <div className="loading-spinner">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <p>Chargement des offres...</p>
                                    </div>
                                )}

                                {error && !loading && (
                                    <div className="error-section">
                                        <div className="error-message">
                                            <i className="fa fa-exclamation-triangle"></i>
                                            <p>{error}</p>
                                            <button className="retry-btn" onClick={fetchOffers}>
                                                <i className="fa fa-refresh"></i>
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!loading && !error && offers.length > 0 && (
                                    <>
                                        <div className="offres-grid">
                                        {offers.length === 0 ? (
                                            <div className="no-offers">
                                                <div className="no-offers-content">
                                                    <i className="fa fa-briefcase"></i>
                                                    <h3>Aucune offre disponible</h3>
                                                    <p>Il n'y a pas d'offres correspondant à vos critères pour le moment.</p>
                                                    <button className="refresh-btn" onClick={fetchOffers}>
                                                        <i className="fa fa-refresh"></i>
                                                        Actualiser
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            offers.map((offer) => (
                                                <div key={offer.id} className="modern-offer-card">
                                                    {/* Status Badge */}
                                                    <div className="offer-status-badge">
                                                        <span className="status-text">ACTIF</span>
                                                    </div>

                                                    {/* Company Logo */}
                                                    <div className="offer-logo-section">
                                                        <div className="offer-logo-container">
                                                            <img 
                                                                src={
                                                                    offer.logoUrl && typeof offer.logoUrl === 'object'
                                                                        ? (offer.logoUrl.small || offer.logoUrl.medium || offer.logoUrl.large || offer.logoUrl.full)
                                                                        : 'https://update.senoffre.com/storage/app/default/picture.jpg'
                                                                } 
                                                                alt={offer.company} 
                                                                className="offer-logo-img"
                                                                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Offer Content */}
                                                    <div className="offer-content">
                                                        <h3 className="offer-title">{offer.title}</h3>
                                                        <p className="offer-company">{offer.company}</p>
                                                        
                                                        {/* Location and Date */}
                                                        <div className="offer-meta">
                                                            <div className="meta-item">
                                                                <i className="fa fa-map-marker-alt"></i>
                                                                <span>{offer.location}</span>
                                                            </div>
                                                            <div className="meta-item">
                                                                <i className="fa fa-calendar"></i>
                                                                <span>{offer.type || 'CDI'}</span>
                                                            </div>
                                                            <div className="meta-item">
                                                                <i className="fa fa-clock"></i>
                                                                <span>{offer.date || new Date().toLocaleDateString('fr-FR')}</span>
                                                            </div>
                                                        </div>

                                                        {/* Tags */}
                                                        <div className="offer-tags">
                                                            <span className="tag tag-category">{offer.category}</span>
                                                            <span className="tag tag-type">FCTA</span>
                                                        </div>

                                                        {/* Description */}
                                                        <div className="offer-description-section">
                                                            <p className="offer-excerpt">
                                                                {offer.description && offer.description.length > 150 
                                                                    ? `${offer.description.substring(0, 150)}...` 
                                                                    : offer.description || offer.excerpt || 'Avis d\'Appel d\'Offres - Cas sans pré qualification'
                                                                }
                                                            </p>
                                                        </div>

                                                        {/* Stats */}
                                                        <div className="offer-stats">
                                                            <div className="stat-item">
                                                                <i className="fa fa-eye"></i>
                                                                <span>{offer.visits || Math.floor(Math.random() * 1000)} vues</span>
                                                            </div>
                                                            <div className="stat-item">
                                                                <i className="fa fa-users"></i>
                                                                <span>{Math.floor(Math.random() * 50)} candidatures</span>
                                                            </div>
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="offer-actions-modern">
                                                            <button 
                                                                className={`action-btn save-btn ${savedPostIds.includes(offer.id) ? 'saved' : ''}`}
                                                                onClick={() => toggleSavePost(offer.id)}
                                                            >
                                                                <i className={`fa ${savedPostIds.includes(offer.id) ? 'fa-heart' : 'fa-heart-o'}`}></i>
                                                                <span>{savedPostIds.includes(offer.id) ? 'Retirer des favoris' : 'Sauvegarder'}</span>
                                                            </button>
                                                            <Link 
                                                                to={`/job-detail?id=${offer.id}`} 
                                                                className="action-btn apply-btn"
                                                            >
                                                                <i className="fa fa-paper-plane"></i>
                                                                <span>Postuler</span>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                        </div>
                                    
                                        {/* Pagination */}
                                        <div className="pagination-section">
                                            <div className="pagination-info">
                                                <span className="results-count">
                                                    Affichage de {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} à {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} sur {pagination.totalItems} offres
                                                </span>
                                                <div className="items-per-page">
                                                    <label>Afficher :</label>
                                                    <select 
                                                        value={pagination.itemsPerPage} 
                                                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                                        className="items-select"
                                                    >
                                                        <option value={6}>6</option>
                                                        <option value={12}>12</option>
                                                        <option value={24}>24</option>
                                                        <option value={48}>48</option>
                                                    </select>
                                                    <span>par page</span>
                                                </div>
                                            </div>
                                            
                                            <div className="pagination-controls">
                                                {/* Bouton Précédent */}
                                                <button 
                                                    className={`pagination-btn prev-btn ${!pagination.hasPrevPage ? 'disabled' : ''}`}
                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                    disabled={!pagination.hasPrevPage}
                                                >
                                                    <i className="fa fa-chevron-left"></i>
                                                    <span>Précédent</span>
                                                </button>
                                                
                                                {/* Numéros de pages */}
                                                <div className="page-numbers">
                                                    {/* Première page */}
                                                    {pagination.currentPage > 3 && (
                                                        <>
                                                            <button 
                                                                className="page-btn"
                                                                onClick={() => handlePageChange(1)}
                                                            >
                                                                1
                                                            </button>
                                                            {pagination.currentPage > 4 && (
                                                                <span className="page-dots">...</span>
                                                            )}
                                                        </>
                                                    )}
                                                    
                                                    {/* Pages autour de la page courante */}
                                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                                        .filter(page => 
                                                            page >= Math.max(1, pagination.currentPage - 2) && 
                                                            page <= Math.min(pagination.totalPages, pagination.currentPage + 2)
                                                        )
                                                        .map(page => (
                                                            <button
                                                                key={page}
                                                                className={`page-btn ${page === pagination.currentPage ? 'active' : ''}`}
                                                                onClick={() => handlePageChange(page)}
                                                            >
                                                                {page}
                                                            </button>
                                                        ))
                                                    }
                                                    
                                                    {/* Dernière page */}
                                                    {pagination.currentPage < pagination.totalPages - 2 && (
                                                        <>
                                                            {pagination.currentPage < pagination.totalPages - 3 && (
                                                                <span className="page-dots">...</span>
                                                            )}
                                                            <button 
                                                                className="page-btn"
                                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                            >
                                                                {pagination.totalPages}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                
                                                {/* Bouton Suivant */}
                                                <button 
                                                    className={`pagination-btn next-btn ${!pagination.hasNextPage ? 'disabled' : ''}`}
                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                    disabled={!pagination.hasNextPage}
                                                >
                                                    <span>Suivant</span>
                                                    <i className="fa fa-chevron-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : activeTab === 'mes-annonces' ? (
                            <div className="posts-section">
                                <div className="section-header">
                                    <h2>MES appels d'offres</h2>
                                    <p>Gérez vos annonces publiées</p>
                                </div>

                                {/* Toolbar: bulk select/delete + search */}
                                <div className="posts-toolbar">
                                    <div className="left-actions">
                                        <label className="checkbox">
                                            <input
                                                type="checkbox"
                                                onChange={toggleSelectPostAll}
                                                checked={(myPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(myPostsSearchQuery.toLowerCase())).length > 0 && (myPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(myPostsSearchQuery.toLowerCase())).every(p => selectedPosts.includes(p.id))}
                                            />
                                            <span>Sélectionnez: Tout</span>
                                        </label>
                                        <button className="btn btn-danger" disabled={selectedPosts.length === 0} onClick={clearPostsSelection}>
                                            <i className="fa fa-trash"></i> Supprimer
                                        </button>
                                    </div>
                                    <div className="right-actions">
                                        <label className="search-label">Rechercher</label>
                                        <input
                                            type="text"
                                            className="posts-search-input"
                                            placeholder="Rechercher par titre ou entreprise..."
                                            value={myPostsSearchQuery}
                                            onChange={(e) => setMyPostsSearchQuery(e.target.value)}
                                        />
                                        {myPostsSearchQuery && (
                                            <button type="button" className="clear-link" onClick={() => setMyPostsSearchQuery('')}>[effacer]</button>
                                        )}
                                    </div>
                                </div>

                                {/* Loading / Error */}
                                {myPostsLoading && (
                                    <div className="companies-loading">
                                        <div className="companies-loader-icon">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <div className="companies-loader-text">Chargement de vos annonces...</div>
                                    </div>
                                )}
                                {myPostsError && !myPostsLoading && (
                                    <div className="error-section">
                                        <div className="error-message">
                                            <i className="fa fa-exclamation-triangle"></i>
                                            <p>{myPostsError}</p>
                                            <button className="retry-btn" onClick={() => fetchMyPosts(1)}>
                                                <i className="fa fa-refresh"></i>
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                {!myPostsLoading && !myPostsError && (
                                    <>
                                        <div className="posts-table">
                                            <div className="table-header">
                                                <div className="th th-photo">Photo</div>
                                                <div className="th th-details">Détails de l'annonce</div>
                                                <div className="th th-salary">Salaire</div>
                                                <div className="th th-actions">Option</div>
                                            </div>

                                            {(myPosts || [])
                                                .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(myPostsSearchQuery.toLowerCase()))
                                                .map((post) => (
                                                    <div key={post.id} className={`table-row ${isPostSelected(post.id) ? 'selected' : ''}`}>
                                                        <div className="td td-photo">
                                                            {post.logo ? (
                                                                <img 
                                                                    className="post-logo" 
                                                                    src={
                                                                        typeof post.logo === 'object'
                                                                            ? (post.logo.small || post.logo.medium || post.logo.large || post.logo.full)
                                                                            : post.logo
                                                                    } 
                                                                    alt={post.companyName}
                                                                    onError={(e) => {
                                                                        // Mettre une image par défaut si l'image ne charge pas
                                                                        e.target.src = 'https://update.senoffre.com/storage/app/default/picture.jpg';
                                                                    }}
                                                                />
                                                            ) : (
                                                                <img 
                                                                    className="post-logo" 
                                                                    src="https://update.senoffre.com/storage/app/default/picture.jpg" 
                                                                    alt={post.companyName}
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="td td-details">
                                                            <div className="post-title">
                                                                <a 
                                                                    href={`/job-detail?id=${post.id}`}
                                                                    style={{ 
                                                                        color: '#000', 
                                                                        textDecoration: 'none', 
                                                                        cursor: 'pointer',
                                                                        fontWeight: '500'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        window.location.href = `/job-detail?id=${post.id}`;
                                                                    }}
                                                                >
                                                                    {post.title}
                                                                </a>
                                                            </div>
                                                            <div className="post-meta">
                                                                <span className="company">{post.companyName}</span>
                                                                {post.category && <span className="divider">•</span>}
                                                                {post.category && <span className="category">{post.category}</span>}
                                                                {post.created_at && <><span className="divider">•</span><span className="date">{new Date(post.created_at).toLocaleDateString('fr-FR')}</span></>}
                                                            </div>
                                                        </div>
                                                        <div className="td td-salary">
                                                            <span className="posts-salary">{post.salary}</span>
                                                        </div>
                                                        <div className="td td-actions">
                                                            <div className="row-actions">
                                                                <label className="checkbox">
                                                                    <input type="checkbox" checked={isPostSelected(post.id)} onChange={() => toggleSelectPostOne(post.id)} />
                                                                </label>
                                                                <button className="btn btn-icon btn-edit" onClick={() => editPost(post)} title="Éditer" aria-label="Éditer">
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-icon btn-danger" onClick={() => deletePost(post)} title="Supprimer" aria-label="Supprimer">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>

                                        {/* Pagination */}
                                        {myPosts.length > 0 && (
                                            <div className="pagination-section">
                                                <div className="pagination-info">
                                                    <span className="results-count">
                                                        Affichage de {((myPostsPagination.currentPage - 1) * myPostsPagination.itemsPerPage) + 1} à {Math.min(myPostsPagination.currentPage * myPostsPagination.itemsPerPage, myPostsPagination.totalItems)} sur {myPostsPagination.totalItems} annonces
                                                    </span>
                                                </div>
                                                <div className="pagination-controls">
                                                    <button className={`pagination-btn prev-btn ${!myPostsPagination.hasPrevPage ? 'disabled' : ''}`} onClick={() => fetchMyPosts(myPostsPagination.currentPage - 1)} disabled={!myPostsPagination.hasPrevPage}>
                                                        <i className="fa fa-chevron-left"></i>
                                                        <span>Précédent</span>
                                                    </button>
                                                    <div className="page-numbers">
                                                        {Array.from({ length: myPostsPagination.totalPages }, (_, i) => i + 1)
                                                            .filter(page => page >= Math.max(1, myPostsPagination.currentPage - 2) && page <= Math.min(myPostsPagination.totalPages, myPostsPagination.currentPage + 2))
                                                            .map(page => (
                                                                <button key={page} className={`page-btn ${page === myPostsPagination.currentPage ? 'active' : ''}`} onClick={() => fetchMyPosts(page)}>
                                                                    {page}
                                                                </button>
                                                            ))}
                                                    </div>
                                                    <button className={`pagination-btn next-btn ${!myPostsPagination.hasNextPage ? 'disabled' : ''}`} onClick={() => fetchMyPosts(myPostsPagination.currentPage + 1)} disabled={!myPostsPagination.hasNextPage}>
                                                        <span>Suivant</span>
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : activeTab === 'en-attente' ? (
                            <div className="posts-section">
                                <div className="section-header">
                                    <h2>En attente</h2>
                                    <p>Vos annonces en cours de validation</p>
                                </div>

                                {/* Toolbar */}
                                <div className="posts-toolbar">
                                    <div className="left-actions">
                                        <label className="checkbox">
                                            <input
                                                type="checkbox"
                                                onChange={toggleSelectPendingAll}
                                                checked={(pendingPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(pendingSearchQuery.toLowerCase())).length > 0 && (pendingPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(pendingSearchQuery.toLowerCase())).every(p => selectedPending.includes(p.id))}
                                            />
                                            <span>Sélectionnez: Tout</span>
                                        </label>
                                        <button className="btn btn-danger" disabled={selectedPending.length === 0} onClick={clearPendingSelection}>
                                            <i className="fa fa-trash"></i> Supprimer
                                        </button>
                                    </div>
                                    <div className="right-actions">
                                        <label className="search-label">Rechercher</label>
                                        <input
                                            type="text"
                                            className="posts-search-input"
                                            placeholder="Rechercher par titre ou entreprise..."
                                            value={pendingSearchQuery}
                                            onChange={(e) => setPendingSearchQuery(e.target.value)}
                                        />
                                        {pendingSearchQuery && (
                                            <button type="button" className="clear-link" onClick={() => setPendingSearchQuery('')}>[effacer]</button>
                                        )}
                                    </div>
                                </div>

                                {/* Loading / Error */}
                                {pendingLoading && (
                                    <div className="companies-loading">
                                        <div className="companies-loader-icon">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <div className="companies-loader-text">Chargement des annonces en attente...</div>
                                    </div>
                                )}
                                {pendingError && !pendingLoading && (
                                    <div className="error-section">
                                        <div className="error-message">
                                            <i className="fa fa-exclamation-triangle"></i>
                                            <p>{pendingError}</p>
                                            <button className="retry-btn" onClick={() => fetchPendingPosts(1)}>
                                                <i className="fa fa-refresh"></i>
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                {!pendingLoading && !pendingError && (
                                    <>
                                        <div className="posts-table">
                                            <div className="table-header">
                                                <div className="th th-photo">Photo</div>
                                                <div className="th th-details">Détails de l'annonce</div>
                                                <div className="th th-salary">Salaire</div>
                                                <div className="th th-actions">Option</div>
                                            </div>

                                            {(pendingPosts || [])
                                                .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(pendingSearchQuery.toLowerCase()))
                                                .map((post) => (
                                                    <div key={post.id} className={`table-row ${isPendingSelected(post.id) ? 'selected' : ''}`}>
                                                        <div className="td td-photo">
                                                            {post.logo ? (
                                                                <img className="post-logo" src={post.logo} alt={post.companyName} />
                                                            ) : (
                                                                <div className="company-logo-placeholder sm">
                                                                    <span className="logo-text">{post.companyName?.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="td td-details">
                                                            <div className="post-title">{post.title}</div>
                                                            <div className="post-meta">
                                                                <span className="company">{post.companyName}</span>
                                                                {post.category && <span className="divider">•</span>}
                                                                {post.category && <span className="category">{post.category}</span>}
                                                                {post.created_at && <><span className="divider">•</span><span className="date">{new Date(post.created_at).toLocaleDateString('fr-FR')}</span></>}
                                                                <span className="divider">•</span>
                                                                <span className="status waiting">En attente</span>
                                                            </div>
                                                        </div>
                                                        <div className="td td-salary">
                                                            <span className="posts-salary">{post.salary}</span>
                                                        </div>
                                                        <div className="td td-actions">
                                                            <div className="row-actions">
                                                                <label className="checkbox">
            							<input type="checkbox" checked={isPendingSelected(post.id)} onChange={() => toggleSelectPendingOne(post.id)} />
                                                                </label>
                                                                <button className="btn btn-icon btn-edit" onClick={() => editPost(post)} title="Éditer" aria-label="Éditer">
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-icon btn-danger" onClick={() => deletePost(post)} title="Supprimer" aria-label="Supprimer">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>

                                        {/* Pagination */}
                                        {pendingPosts.length > 0 && (
                                            <div className="pagination-section">
                                                <div className="pagination-info">
                                                    <span className="results-count">
                                                        Affichage de {((pendingPagination.currentPage - 1) * pendingPagination.itemsPerPage) + 1} à {Math.min(pendingPagination.currentPage * pendingPagination.itemsPerPage, pendingPagination.totalItems)} sur {pendingPagination.totalItems} annonces en attente
                                                    </span>
                                                </div>
                                                <div className="pagination-controls">
                                                    <button className={`pagination-btn prev-btn ${!pendingPagination.hasPrevPage ? 'disabled' : ''}`} onClick={() => fetchPendingPosts(pendingPagination.currentPage - 1)} disabled={!pendingPagination.hasPrevPage}>
                                                        <i className="fa fa-chevron-left"></i>
                                                        <span>Précédent</span>
                                                    </button>
                                                    <div className="page-numbers">
                                                        {Array.from({ length: pendingPagination.totalPages }, (_, i) => i + 1)
                                                            .filter(page => page >= Math.max(1, pendingPagination.currentPage - 2) && page <= Math.min(pendingPagination.totalPages, pendingPagination.currentPage + 2))
                                                            .map(page => (
                                                                <button key={page} className={`page-btn ${page === pendingPagination.currentPage ? 'active' : ''}`} onClick={() => fetchPendingPosts(page)}>
                                                                    {page}
                                                                </button>
                                                            ))}
                                                    </div>
                                                    <button className={`pagination-btn next-btn ${!pendingPagination.hasNextPage ? 'disabled' : ''}`} onClick={() => fetchPendingPosts(pendingPagination.currentPage + 1)} disabled={!pendingPagination.hasNextPage}>
                                                        <span>Suivant</span>
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : activeTab === 'mes-archives' ? (
                            <div className="posts-section">
                                <div className="section-header">
                                    <h2>Mes archives</h2>
                                    <p>Vos annonces archivées</p>
                                </div>

                                {/* Toolbar */}
                                <div className="posts-toolbar">
                                    <div className="left-actions">
                                        <label className="checkbox">
                                            <input
                                                type="checkbox"
                                                onChange={toggleSelectArchivedAll}
                                                checked={(archivedPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(archivedSearchQuery.toLowerCase())).length > 0 && (archivedPosts || []).filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(archivedSearchQuery.toLowerCase())).every(p => selectedArchived.includes(p.id))}
                                            />
                                            <span>Sélectionnez: Tout</span>
                                        </label>
                                        <button className="btn btn-danger" disabled={selectedArchived.length === 0} onClick={clearArchivedSelection}>
                                            <i className="fa fa-trash"></i> Supprimer
                                        </button>
                                    </div>
                                    <div className="right-actions">
                                        <label className="search-label">Rechercher</label>
                                        <input
                                            type="text"
                                            className="posts-search-input"
                                            placeholder="Rechercher par titre ou entreprise..."
                                            value={archivedSearchQuery}
                                            onChange={(e) => setArchivedSearchQuery(e.target.value)}
                                        />
                                        {archivedSearchQuery && (
                                            <button type="button" className="clear-link" onClick={() => setArchivedSearchQuery('')}>[effacer]</button>
                                        )}
                                    </div>
                                </div>

                                {/* Loading / Error */}
                                {archivedLoading && (
                                    <div className="companies-loading">
                                        <div className="companies-loader-icon">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <div className="companies-loader-text">Chargement des archives...</div>
                                    </div>
                                )}
                                {archivedError && !archivedLoading && (
                                    <div className="error-section">
                                        <div className="error-message">
                                            <i className="fa fa-exclamation-triangle"></i>
                                            <p>{archivedError}</p>
                                            <button className="retry-btn" onClick={() => fetchArchivedPosts(1)}>
                                                <i className="fa fa-refresh"></i>
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Table */}
                                {!archivedLoading && !archivedError && (
                                    <>
                                        <div className="posts-table">
                                            <div className="table-header">
                                                <div className="th th-photo">Photo</div>
                                                <div className="th th-details">Détails de l'annonce</div>
                                                <div className="th th-salary">Salaire</div>
                                                <div className="th th-actions">Option</div>
                                            </div>

                                            {(archivedPosts || [])
                                                .filter(p => `${p.title} ${p.companyName}`.toLowerCase().includes(archivedSearchQuery.toLowerCase()))
                                                .map((post) => (
                                                    <div key={post.id} className={`table-row ${isArchivedSelected(post.id) ? 'selected' : ''}`}>
                                                        <div className="td td-photo">
                                                            {post.logo ? (
                                                                <img className="post-logo" src={post.logo} alt={post.companyName} />
                                                            ) : (
                                                                <div className="company-logo-placeholder sm">
                                                                    <span className="logo-text">{post.companyName?.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="td td-details">
                                                            <div className="post-title">{post.title}</div>
                                                            <div className="post-meta">
                                                                <span className="company">{post.companyName}</span>
                                                                {post.category && <span className="divider">•</span>}
                                                                {post.category && <span className="category">{post.category}</span>}
                                                                {post.created_at && <><span className="divider">•</span><span className="date">{new Date(post.created_at).toLocaleDateString('fr-FR')}</span></>}
                                                                <span className="divider">•</span>
                                                                <span className="status archived">Archivé</span>
                                                            </div>
                                                        </div>
                                                        <div className="td td-salary">
                                                            <span className="posts-salary">{post.salary}</span>
                                                        </div>
                                                        <div className="td td-actions">
                                                            <div className="row-actions">
                                                                <label className="checkbox">
                                                                    <input type="checkbox" checked={isArchivedSelected(post.id)} onChange={() => toggleSelectArchivedOne(post.id)} />
                                                                </label>
                                                                <button className="btn btn-icon btn-edit" onClick={() => editPost(post)} title="Éditer" aria-label="Éditer">
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-icon btn-danger" onClick={() => deletePost(post)} title="Supprimer" aria-label="Supprimer">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>

                                        {/* Pagination */}
                                        {archivedPosts.length > 0 && (
                                            <div className="pagination-section">
                                                <div className="pagination-info">
                                                    <span className="results-count">
                                                        Affichage de {((archivedPagination.currentPage - 1) * archivedPagination.itemsPerPage) + 1} à {Math.min(archivedPagination.currentPage * archivedPagination.itemsPerPage, archivedPagination.totalItems)} sur {archivedPagination.totalItems} annonces archivées
                                                    </span>
                                                </div>
                                                <div className="pagination-controls">
                                                    <button className={`pagination-btn prev-btn ${!archivedPagination.hasPrevPage ? 'disabled' : ''}`} onClick={() => fetchArchivedPosts(archivedPagination.currentPage - 1)} disabled={!archivedPagination.hasPrevPage}>
                                                        <i className="fa fa-chevron-left"></i>
                                                        <span>Précédent</span>
                                                    </button>
                                                    <div className="page-numbers">
                                                        {Array.from({ length: archivedPagination.totalPages }, (_, i) => i + 1)
                                                            .filter(page => page >= Math.max(1, archivedPagination.currentPage - 2) && page <= Math.min(archivedPagination.totalPages, archivedPagination.currentPage + 2))
                                                            .map(page => (
                                                                <button key={page} className={`page-btn ${page === archivedPagination.currentPage ? 'active' : ''}`} onClick={() => fetchArchivedPosts(page)}>
                                                                    {page}
                                                                </button>
                                                            ))}
                                                    </div>
                                                    <button className={`pagination-btn next-btn ${!archivedPagination.hasNextPage ? 'disabled' : ''}`} onClick={() => fetchArchivedPosts(archivedPagination.currentPage + 1)} disabled={!archivedPagination.hasNextPage}>
                                                        <span>Suivant</span>
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : activeTab === 'messenger' ? (
                            <div className="offres-section">
                                <div className="section-header">
                                    <h2>Messenger</h2>
                                    <p>Gérez vos conversations</p>
                                </div>
                                <MessagesPage apiEndpoint={'axiosClient_BASE'} />
                            </div>
                        ) : activeTab === 'mon-compte' ? (
                            <div className="entreprises-section">
                                <div className="section-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                    <div>
                                        <h2>Bonjour {userProfile.name || '—'} !</h2>
                                        <p>Votre dernière connexion remonte à {userProfile.lastLogin || '-'}</p>
                                    </div>
                                    <div>
                                        <button type="button" className="btn btn-danger" onClick={confirmLogout}>
                                            <i className="fa fa-sign-out"></i> Déconnexion
                                        </button>
                                    </div>
                                </div>

                                {/* Photo / Avatar */}
                                <div className="companies-toolbar" style={{marginBottom: 20}}>
                                    <div style={{display:'flex', alignItems:'center', gap:16}}>
                                        <div className="company-logo-placeholder sm" style={{width:100,height:100,borderRadius:12, overflow:'hidden'}}>
                                            {account.photo_url ? (
                                                <img src={account.photo_url} alt="avatar" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={(ev)=>{ ev.currentTarget.style.display='none'; }} />
                                            ) : null}
                                            {!account.photo_url && (
                                                <span className="logo-text">{(userProfile.name||'--').split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                            <div style={{display:'flex', gap:8, alignItems:'center'}}>
                                                <button type="button" className="btn btn-edit" onClick={handlePickPhoto} disabled={photoUploading}>
                                                    <i className="fa fa-folder-open"></i> Parcourir ...
                                                </button>
                                                {photoUploading && <i className="fa fa-spinner fa-spin" aria-label="uploading" />}
                                            </div>
                                            {photoError && (
                                                <div className="error-message inline"><i className="fa fa-exclamation-triangle"></i><span>{photoError}</span></div>
                                            )}
                                            {photoSuccess && (
                                                <div className="success-message inline"><i className="fa fa-check-circle"></i><span>{photoSuccess}</span></div>
                                            )}
                                            <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={handlePhotoChange} />
                                        </div>
                                    </div>
                                </div>

                                {/* Détails du compte */}
                                <div className="companies-table" style={{padding:16}}>
                                    {accountLoading ? (
                                        <div className="companies-loading"><div className="companies-loader-icon"><i className="fa fa-spinner fa-spin"></i></div><div className="companies-loader-text">Chargement du compte...</div></div>
                                    ) : accountError ? (
                                        <div className="error-section"><div className="error-message"><i className="fa fa-exclamation-triangle"></i><p>{accountError}</p><button className="retry-btn" onClick={fetchUserProfile}><i className="fa fa-refresh"></i>Réessayer</button></div></div>
                                    ) : (
                                        <div className="form-grid">
                                            {accountSuccess && (
                                                <div className="success-message" style={{marginBottom:12}}>
                                                    <i className="fa fa-check-circle"></i>
                                                    <span>{accountSuccess}</span>
                                                </div>
                                            )}
                                            <div className="form-group">
                                                <label>Civilité</label>
                                                <select value={account.civility} onChange={(e)=>setAccount(a=>({...a,civility:e.target.value}))}>
                                                    <option value="">Sélectionnez</option>
                                                    <option value="M">Monsieur</option>
                                                    <option value="F">Madame</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Nom</label>
                                                <input type="text" value={account.name} onChange={(e)=>setAccount(a=>({...a,name:e.target.value}))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Nom d'utilisateur</label>
                                                <input type="text" value={account.username} onChange={(e)=>setAccount(a=>({...a,username:e.target.value}))} placeholder="Nom d'utilisateur" />
                                            </div>
                                            <div className="form-group form-group-full">
                                                <label>Email</label>
                                                <input type="email" value={account.email} onChange={(e)=>setAccount(a=>({...a,email:e.target.value}))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Téléphone</label>
                                                <div style={{display:'flex', gap:8, alignItems:'center'}}>
                                                    <select value={account.phone_country} onChange={(e)=>setAccount(a=>({...a,phone_country:e.target.value}))}>
                                                        <option value="SN">SN (+221)</option>
                                                    </select>
                                                    <input type="text" value={maskPhone && account.phone ? account.phone.replace(/.(?=.{2})/g,'•') : account.phone} onChange={(e)=>setAccount(a=>({...a,phone:e.target.value}))} placeholder="70 123 45 67" />
                                                    <label className="checkbox" style={{margin:0}}>
                                                        <input type="checkbox" checked={maskPhone} onChange={(e)=>setMaskPhone(e.target.checked)} />
                                                        <span>Cacher</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="form-group form-group-full" style={{marginTop:8}}>
                                                <button type="button" className="btn" onClick={updateProfile} disabled={accountLoading}>
                                                    Mettre à jour
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Paramètres */}
                                <div className="companies-table" style={{padding:16, marginTop:16}}>
                                    <div className="section-subheader" style={{marginBottom:12}}>
                                        <h3 style={{margin:0}}>Paramètres</h3>
                                    </div>
                                    <div className="form-grid">
                                        {settingsSuccess && (
                                            <div className="success-message" style={{marginBottom:12}}>
                                                <i className="fa fa-check-circle"></i>
                                                <span>{settingsSuccess}</span>
                                            </div>
                                        )}
                                        <div className="form-group">
                                            <label>Nouveau mot de passe</label>
                                            <input type="password" value={settings.password} onChange={(e)=>setSettings(s=>({...s,password:e.target.value}))} placeholder="Mot de passe" />
                                        </div>
                                        <div className="form-group">
                                            <label>Confirmez le mot de passe</label>
                                            <input type="password" value={settings.password_confirmation} onChange={(e)=>setSettings(s=>({...s,password_confirmation:e.target.value}))} placeholder="Confirmez le mot de passe" />
                                        </div>
                                        <div className="form-group form-group-full">
                                            <label className="checkbox">
                                                <input type="checkbox" checked={settings.marketing_opt_in} onChange={(e)=>setSettings(s=>({...s,marketing_opt_in:e.target.checked}))} />
                                                <span>J'accepte de recevoir des e-mails marketing</span>
                                            </label>
                                        </div>
                                        <div className="form-group">
                                            <label>Fuseau horaire préféré</label>
                                            <select value={settings.timezone} onChange={(e)=>setSettings(s=>({...s,timezone:e.target.value}))}>
                                                <option value="">Sélectionnez un fuseau horaire</option>
                                                <option value="Africa/Dakar">Africa/Dakar</option>
                                                <option value="UTC">UTC</option>
                                                <option value="Europe/Paris">Europe/Paris</option>
                                                <option value="America/New_York">America/New_York</option>
                                            </select>
                                            <small>REMARQUE: Si aucun fuseau n'est sélectionné, celui du pays sera utilisé (ex: "Africa/Dakar").</small>
                                        </div>
                                        <div className="form-group form-group-full" style={{marginTop:8}}>
                                            <button type="button" className="btn" onClick={updateSettings}>
                                                Mettre à jour
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : activeTab === 'mes-entreprises' ? (
                            <div className="entreprises-section">
                                <div className="section-header">
                                    <div>
                                        <h2>Mes Entreprises</h2>
                                        <p>Gérez vos entreprises et leurs Appels d'offres</p>
                                    </div>
                                    <div>
                                        <button type="button" className="action-btn" onClick={openCompanyModal}>
                                            <i className="fa fa-plus"></i>
                                            <span>Créer une nouvelle entreprise</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Loading State */}
                                {companiesLoading && (
                                    <div className="companies-loading">
                                        <div className="companies-loader-icon">
                                            <i className="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <div className="companies-loader-text">Chargement de vos entreprises...</div>
                                    </div>
                                )}

                                {/* Error State */}
                                {companiesError && (
                                    <div className="error-section">
                                        <div className="error-message">
                                            <i className="fa fa-exclamation-triangle"></i>
                                            <p>{companiesError}</p>
                                            <button className="retry-btn" onClick={() => fetchCompanies(1)}>
                                                <i className="fa fa-refresh"></i>
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Companies Grid */}
                                {!companiesLoading && !companiesError && (
                                    <>
                                        {companies.length === 0 ? (
                                            <div className="no-companies">
                                                <div className="no-companies-content">
                                                    <i className="fa fa-building"></i>
                                                    <h3>Aucune entreprise trouvée</h3>
                                                    <p>Vous n'avez pas encore d'entreprises enregistrées.</p>
                                                    <button type="button" className="add-company-btn" onClick={openCompanyModal}>
                                                        <i className="fa fa-plus"></i>
                                                        Ajouter une entreprise
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Toolbar: bulk, delete, search */}
                                                <div className="companies-toolbar">
                                                    <div className="left-actions">
                                                        <label className="checkbox">
                                                            <input 
                                                                type="checkbox" 
                                                                onChange={toggleSelectAll}
                                                                checked={(companies || []).filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase())).length > 0 && (companies || []).filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase())).every(c => selectedCompanies.includes(c.id))}
                                                            />
                                                            <span>Sélectionnez: Tout</span>
                                                        </label>
                                                        <button className="btn btn-danger" disabled={selectedCompanies.length === 0} onClick={clearSelection}>
                                                            <i className="fa fa-trash"></i> Supprimer
                                                        </button>
                                                    </div>
                                                    <div className="right-actions">
                                                        <label className="search-label">Rechercher</label>
                                                        <input 
                                                            type="text" 
                                                            className="company-search-input" 
                                                            placeholder="Rechercher par nom..."
                                                            value={companySearchQuery}
                                                            onChange={(e) => setCompanySearchQuery(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Table-like list */}
                                                <div className="companies-table">
                                                    <div className="table-header">
                                                        <div className="th th-check"></div>
                                                        <div className="th th-logo">Logo</div>
                                                        <div className="th th-name">Raison sociale / Description</div>
                                                        <div className="th th-posts">Annonces</div>
                                                        <div className="th th-actions">Option</div>
                                                    </div>

                                                    {(companies || [])
                                                        .filter(c => c.name.toLowerCase().includes(companySearchQuery.toLowerCase()))
                                                        .map((company) => (
                                                        <div key={company.id} className={`table-row ${isSelected(company.id) ? 'selected' : ''}`}>
                                                            <div className="td td-check">
                                                                <input type="checkbox" checked={isSelected(company.id)} onChange={() => toggleSelectOne(company.id)} />
                                                            </div>
                                                            <div className="td td-logo">
                                                                {company.logo ? (
                                                                    <img className="company-logo-table" src={company.logo} alt={company.name} />
                                                                ) : (
                                                                    <div className="company-logo-placeholder sm">
                                                                        <span className="logo-text">
                                                                            {company.name.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="td td-name">
                                                                <div className="company-name-link">{company.name}</div>
                                                                {company.description && (
                                                                    <div className="company-desc">
                                                                        <i className="fa fa-info-circle"></i> {company.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="td td-posts">
                                                                <span className="posts-count">{company.posts_count || 0}</span>
                                                            </div>
                                                            <div className="td td-actions">
                                                                <button className="btn btn-icon btn-edit" onClick={() => openEditCompanyModal(company)} title="Éditer" aria-label="Éditer">
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-icon btn-danger" onClick={() => deleteCompany(company)} title="Supprimer" aria-label="Supprimer">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {/* Companies Pagination */}
                                        {companies.length > 0 && (
                                            <div className="pagination-section">
                                                <div className="pagination-info">
                                                    <span className="results-count">
                                                        Affichage de {((companiesPagination.currentPage - 1) * companiesPagination.itemsPerPage) + 1} à {Math.min(companiesPagination.currentPage * companiesPagination.itemsPerPage, companiesPagination.totalItems)} sur {companiesPagination.totalItems} entreprises
                                                    </span>
                                                </div>
                                                
                                                <div className="pagination-controls">
                                                    <button 
                                                        className={`pagination-btn prev-btn ${!companiesPagination.hasPrevPage ? 'disabled' : ''}`}
                                                        onClick={() => fetchCompanies(companiesPagination.currentPage - 1)}
                                                        disabled={!companiesPagination.hasPrevPage}
                                                    >
                                                        <i className="fa fa-chevron-left"></i>
                                                        <span>Précédent</span>
                                                    </button>
                                                    
                                                    <div className="page-numbers">
                                                        {Array.from({ length: companiesPagination.totalPages }, (_, i) => i + 1)
                                                            .filter(page => 
                                                                page >= Math.max(1, companiesPagination.currentPage - 2) && 
                                                                page <= Math.min(companiesPagination.totalPages, companiesPagination.currentPage + 2)
                                                            )
                                                            .map(page => (
                                                                <button
                                                                    key={page}
                                                                    className={`page-btn ${page === companiesPagination.currentPage ? 'active' : ''}`}
                                                                    onClick={() => fetchCompanies(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            ))
                                                        }
                                                    </div>
                                                    
                                                    <button 
                                                        className={`pagination-btn next-btn ${!companiesPagination.hasNextPage ? 'disabled' : ''}`}
                                                        onClick={() => fetchCompanies(companiesPagination.currentPage + 1)}
                                                        disabled={!companiesPagination.hasNextPage}
                                                    >
                                                        <span>Suivant</span>
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="welcome-section">
                                <div className="success-message">
                                    <i className="fa fa-check-circle"></i>
                                    <span>Félicitation Block Général ! Votre Adresse Email a été vérifié avec succès.</span>
                                </div>
                                
                                <div className="profile-section">
                                    <div className="profile-info">
                                        <div className="profile-avatar">
                                            <i className="fa fa-user-circle"></i>
                                        </div>
                                        <div className="profile-details">
                                            <h2>Bonjour Block Général !</h2>
                                            <p>Votre dernière connexion remonte à 2025-09-28</p>
                                        </div>
                                    </div>
                                    
                                    <div className="avatar-upload">
                                        <div className="upload-section">
                                            <h3>Photo ou Avatar</h3>
                                            <div className="upload-area">
                                                <i className="fa fa-camera"></i>
                                                <p>Votre Photo<br/>ou Avatar</p>
                                                <small>Cliquer pour<br/>sélectionner</small>
                                            </div>
                                            <button className="upload-btn">
                                                <i className="fa fa-search"></i>
                                                En Parcourir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Create Post Modal */}
                {showPostModal && (
                    <div className="modal-overlay">
                        <div className="modal-container">
                            <div className="modal-header">
                                <h3>{editingPostId ? 'Modifier une offre' : 'Publier une offre'}</h3>
                                <button className="modal-close" onClick={() => { 
                                    setShowPostModal(false); 
                                    setPostError(''); 
                                    setPostSuccess(''); 
                                    setEditingPostId(null);
                                    // Reset form when closing
                                    setPostForm({
                                        country_code: 'SN',
                                        title: '',
                                        description: '',
                                        category_id: '',
                                        post_type_id: '',
                                        city_id: '',
                                        contact_name: '',
                                        email: '',
                                        accept_terms: false,
                                        package_id: '',
                                        tags: '',
                                        company_name: '',
                                        company_description: '',
                                        selected_company_id: '',
                                        startup_date: '',
                                        phone_country: '+221',
                                        phone: '',
                                        hide_phone: false,
                                        application_url: ''
                                    });
                                }}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                {postError && (
                                    <div className="error-message inline">
                                        <i className="fa fa-exclamation-triangle"></i>
                                        <span>{postError}</span>
                                    </div>
                                )}
                                {postSuccess && (
                                    <div className="success-message inline">
                                        <i className="fa fa-check-circle"></i>
                                        <span>{postSuccess}</span>
                                    </div>
                                )}

                                {/* Section: Information sur la société */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <i className="fa fa-building"></i>
                                        <h4>Information sur la société</h4>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group form-group-full">
                                            <label>Sélectionnez une entreprise <span className="required">*</span></label>
                                            <select
                                                value={postForm.selected_company_id}
                                                onChange={(e) => setPostForm(p => ({ ...p, selected_company_id: e.target.value }))}
                                            >
                                                <option value="">Sélectionnez une entreprise</option>
                                                {(companies || []).map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Détails de l'annonce */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <i className="fa fa-file-alt"></i>
                                        <h4>Détails de l'annonce</h4>
                                    </div>
                                    <div className="form-grid">
                                        <div className="form-group form-group-full">
                                            <label>Catégorie <span className="required">*</span></label>
                                            <select
                                                value={postForm.category_id}
                                                onChange={(e) => setPostForm(p => ({ ...p, category_id: e.target.value }))}
                                                disabled={categoriesLoading}
                                            >
                                                <option value="">Sélectionnez une catégorie</option>
                                                {categories.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            {categoriesLoading && <small>Chargement des catégories...</small>}
                                        </div>
                                        
                                        <div className="form-group form-group-full">
                                            <label>Titre <span className="required">*</span></label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-heading"></i>
                                                <input 
                                                    type="text" 
                                                    value={postForm.title} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, title: e.target.value }))} 
                                                    placeholder="Ex: Développeur Frontend React - CDI" 
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                            <small>Un bon titre doit avoir au moins 60 caractères.</small>
                                        </div>
                                        
                                        <div className="form-group form-group-full">
                                            <label>Description <span className="required">*</span></label>
                                            <div className="classic-editor">
                                                <div className="editor-toolbar">
                                                    <button type="button" title="Annuler" onClick={() => document.execCommand('undo')}>
                                                        <i className="fa fa-undo"></i>
                                                    </button>
                                                    <button type="button" title="Refaire" onClick={() => document.execCommand('redo')}>
                                                        <i className="fa fa-redo"></i>
                                                    </button>
                                                    <button type="button" title="Gras" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('bold', false, null);
                                                    }}>
                                                        <i className="fa fa-bold"></i>
                                                    </button>
                                                    <button type="button" title="Italique" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('italic', false, null);
                                                    }}>
                                                        <i className="fa fa-italic"></i>
                                                    </button>
                                                    <button type="button" title="Souligné" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('underline', false, null);
                                                    }}>
                                                        <i className="fa fa-underline"></i>
                                                    </button>
                                                    <button type="button" title="Couleur du texte" className="color-btn">
                                                        <span style={{color: '#000', fontSize: '16px'}}>A</span>
                                                        <i className="fa fa-caret-down" style={{fontSize: '10px'}}></i>
                                                    </button>
                                                    <button type="button" title="Couleur de fond" className="color-btn">
                                                        <i className="fa fa-paint-brush" style={{color: '#ffff00'}}></i>
                                                        <i className="fa fa-caret-down" style={{fontSize: '10px'}}></i>
                                                    </button>
                                                    <button type="button" title="Liste à puces" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('insertUnorderedList', false, null);
                                                    }}>
                                                        <i className="fa fa-list-ul"></i>
                                                    </button>
                                                    <button type="button" title="Liste numérotée" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('insertOrderedList', false, null);
                                                    }}>
                                                        <i className="fa fa-list-ol"></i>
                                                    </button>
                                                    <button type="button" title="Citation" onClick={() => document.execCommand('formatBlock', false, 'blockquote')}>
                                                        <i className="fa fa-quote-left"></i>
                                                    </button>
                                                    <button type="button" title="Tableau" className="table-btn">
                                                        <i className="fa fa-table"></i>
                                                        <i className="fa fa-caret-down" style={{fontSize: '10px'}}></i>
                                                    </button>
                                                    <button type="button" title="Lien" onClick={() => {
                                                        const url = prompt('Entrez l\'URL:');
                                                        if (url) document.execCommand('createLink', false, url);
                                                    }}>
                                                        <i className="fa fa-link"></i>
                                                    </button>
                                                    <button type="button" title="Supprimer le lien" onClick={() => document.execCommand('unlink')}>
                                                        <i className="fa fa-unlink"></i>
                                                    </button>
                                                    <button type="button" title="Aligner à gauche" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('justifyLeft', false, null);
                                                    }}>
                                                        <i className="fa fa-align-left"></i>
                                                    </button>
                                                    <button type="button" title="Centrer" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('justifyCenter', false, null);
                                                    }}>
                                                        <i className="fa fa-align-center"></i>
                                                    </button>
                                                    <button type="button" title="Aligner à droite" onClick={() => {
                                                        const editor = document.querySelector('.editor-content');
                                                        editor.focus();
                                                        document.execCommand('justifyRight', false, null);
                                                    }}>
                                                        <i className="fa fa-align-right"></i>
                                                    </button>
                                                    <button type="button" title="Diminuer l'indentation" onClick={() => document.execCommand('outdent')}>
                                                        <i className="fa fa-outdent"></i>
                                                    </button>
                                                    <button type="button" title="Augmenter l'indentation" onClick={() => document.execCommand('indent')}>
                                                        <i className="fa fa-indent"></i>
                                                    </button>
                                                    <button type="button" title="Plus d'options">
                                                        <i className="fa fa-ellipsis-h"></i>
                                                    </button>
                                                </div>
                                                <div 
                                                    className="editor-content"
                                                    contentEditable={true}
                                                    onInput={(e) => setPostForm(p => ({ ...p, description: e.target.innerHTML }))}
                                                    onBlur={(e) => setPostForm(p => ({ ...p, description: e.target.innerHTML }))}
                                                    data-placeholder="Décrivez les missions, les compétences requises, les avantages..."
                                                    style={{ 
                                                        minHeight: '200px', 
                                                        padding: '12px', 
                                                        border: 'none', 
                                                        outline: 'none',
                                                        direction: 'ltr',
                                                        textAlign: 'left',
                                                        unicodeBidi: 'embed'
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>Type d'emploi <span className="required">*</span></label>
                                            <select
                                                value={postForm.post_type_id}
                                                onChange={(e) => setPostForm(p => ({ ...p, post_type_id: e.target.value }))}
                                                disabled={postTypesLoading}
                                            >
                                                <option value="">Sélectionnez un type</option>
                                                {postTypes.map(pt => (
                                                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                                                ))}
                                            </select>
                                            {postTypesLoading && <small>Chargement des types...</small>}
                                        </div>


                                        <div className="form-group form-group-full">
                                            <label>Démarrage</label>
                                            <div className="date-picker-wrapper">
                                                <i className="fa fa-calendar-alt date-icon"></i>
                                                <input 
                                                    type="date" 
                                                    value={postForm.startup_date || ''} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, startup_date: e.target.value }))} 
                                                    min={new Date().toISOString().split('T')[0]}
                                                    className="date-input"
                                                    placeholder="Date de début"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>Nom du contact <span className="required">*</span></label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-user"></i>
                                                <input 
                                                    type="text" 
                                                    value={postForm.contact_name} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, contact_name: e.target.value }))} 
                                                    placeholder="Ex: M. Babacar Gueye - Responsable RH" 
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>Email <span className="required">*</span></label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-envelope"></i>
                                                <input 
                                                    type="email" 
                                                    value={postForm.email} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, email: e.target.value }))} 
                                                    placeholder="rh@entreprise.com" 
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>N° de téléphone</label>
                                            <div className="phone-simple-container">
                                                <div className="input-with-icon phone-country-wrapper">
                                                    <i className="fa fa-globe"></i>
                                                    <input 
                                                        type="text" 
                                                        className="phone-country-input"
                                                        value={postForm.phone_country} 
                                                        onChange={(e) => setPostForm(p => ({ ...p, phone_country: e.target.value }))} 
                                                        placeholder="+221"
                                                        style={{ paddingLeft: '35px' }}
                                                    />
                                                </div>
                                                <div className="input-with-icon phone-number-wrapper">
                                                    <i className="fa fa-phone"></i>
                                                    <input 
                                                        type="tel" 
                                                        className="phone-number-input"
                                                        value={postForm.phone} 
                                                        onChange={(e) => setPostForm(p => ({ ...p, phone: e.target.value }))} 
                                                        placeholder="77 123 45 67" 
                                                        style={{ paddingLeft: '35px' }}
                                                    />
                                                </div>
                                                <div className="checkbox-field">
                                                    <input 
                                                        type="checkbox" 
                                                        id="hide-phone" 
                                                        checked={postForm.hide_phone}
                                                        onChange={(e) => setPostForm(p => ({ ...p, hide_phone: e.target.checked }))}
                                                    />
                                                    <label htmlFor="hide-phone">Cacher</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>Ville <span className="required">*</span></label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-map-marker-alt"></i>
                                                <select
                                                    value={postForm.city_id}
                                                    onChange={(e) => setPostForm(p => ({ ...p, city_id: e.target.value }))}
                                                >
                                                    <option value="">Sélectionnez une ville</option>
                                                    {cities.map((c) => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>URL de candidature</label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-link"></i>
                                                <input 
                                                    type="url" 
                                                    value={postForm.application_url} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, application_url: e.target.value }))} 
                                                    placeholder="https://exemple.com/postuler" 
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                            <small>Les candidats suivront cette adresse URL pour postuler à l'annonce.</small>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <label>Mots clés</label>
                                            <div className="input-with-icon">
                                                <i className="fa fa-tags"></i>
                                                <textarea 
                                                    rows={3} 
                                                    value={postForm.tags} 
                                                    onChange={(e) => setPostForm(p => ({ ...p, tags: e.target.value }))} 
                                                    placeholder="React, JavaScript, Frontend, UI/UX, Agilité..."
                                                    style={{ paddingLeft: '35px' }}
                                                />
                                            </div>
                                            <small>Saisissez les mots-clés séparés par des virgules ou appuyez sur la touche Entrée (↵) de votre clavier après chaque mot-clé. Le nombre de mots-clés ne peut pas dépasser 15. Et chaque mot-clé ne peut comporter que 2 à 30 caractères.</small>
                                        </div>

                                        <div className="form-group form-group-full">
                                            <div className="checkbox-field">
                                                <input 
                                                    type="checkbox" 
                                                    id="accept-terms" 
                                                    checked={postForm.accept_terms}
                                                    onChange={(e) => setPostForm(p => ({ ...p, accept_terms: e.target.checked }))}
                                                />
                                                <label htmlFor="accept-terms">
                                                    J'accepte les <a href="#" target="_blank">termes et conditions</a> <span className="required">*</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn secondary" onClick={() => { 
                                    setShowPostModal(false); 
                                    setPostError(''); 
                                    setPostSuccess(''); 
                                    setEditingPostId(null);
                                    // Reset form when closing
                                    setPostForm({
                                        country_code: 'SN',
                                        title: '',
                                        description: '',
                                        category_id: '',
                                        post_type_id: '',
                                        city_id: '',
                                        contact_name: '',
                                        email: '',
                                        accept_terms: false,
                                        package_id: '',
                                        tags: '',
                                        company_name: '',
                                        company_description: '',
                                        selected_company_id: '',
                                        startup_date: '',
                                        phone_country: '+221',
                                        phone: '',
                                        hide_phone: false,
                                        application_url: ''
                                    });
                                }} disabled={postSubmitting}>
                                    Annuler
                                </button>
                                <button className="btn primary submit-btn" disabled={postSubmitting} onClick={async () => {
                                    setPostError('');
                                    setPostSuccess('');
                                    // Validation des champs requis
                                    const requiredFields = [
                                        { field: 'title', message: 'Veuillez renseigner le titre.' },
                                        { field: 'description', message: 'Veuillez renseigner la description.' },
                                        { field: 'contact_name', message: 'Veuillez renseigner le nom du contact.' },
                                        { field: 'email', message: 'Veuillez renseigner l\'email.' },
                                        { field: 'selected_company_id', message: 'Veuillez sélectionner une entreprise.' },
                                        { field: 'category_id', message: 'Veuillez sélectionner une catégorie.' },
                                        { field: 'post_type_id', message: 'Veuillez sélectionner un type d\'emploi.' },
                                        { field: 'city_id', message: 'Veuillez sélectionner une ville.' }
                                    ];
                                    
                                    for (const { field, message } of requiredFields) {
                                        if (!postForm[field]) {
                                            setPostError(message);
                                            return;
                                        }
                                    }
                                    
                                    if (!postForm.accept_terms) {
                                        setPostError('Veuillez accepter les termes et conditions.');
                                        return;
                                    }
                                    try {
                                        setPostSubmitting(true);
                                        
                                        // Get description from Editor.js
                                        const editorDescription = await getEditorData();
                                        
                                        // Find selected company info
                                        const selectedCompany = companies.find(c => c.id == postForm.selected_company_id);
                                        if (!selectedCompany) {
                                            setPostError("Entreprise sélectionnée introuvable.");
                                            return;
                                        }
                                        
                                        const payload = {
                                            country_code: postForm.country_code || 'SN',
                                            title: postForm.title,
                                            description: editorDescription || postForm.description,
                                            category_id: Number(postForm.category_id),
                                            post_type_id: Number(postForm.post_type_id),
                                            city_id: Number(postForm.city_id),
                                            contact_name: postForm.contact_name,
                                            email: postForm.email,
                                            accept_terms: true,
                                            package_id: Number(postForm.package_id) || 1,
                                            tags: postForm.tags || '',
                                            company: {
                                                name: selectedCompany.name,
                                                description: selectedCompany.description || 'Description de l\'entreprise'
                                            }
                                        };
                                        
                                        if (editingPostId) {
                                            // Update existing post using PUT
                                            const updateUrl = `${API_CONFIG.BASE_URL}/posts/${editingPostId}`;
                                            console.log('🔧 [DEBUG] Updating post URL:', updateUrl);
                                            console.log('🔧 [DEBUG] Payload:', payload);
                                            
                                            await axiosClient.put(updateUrl, payload, {
                                                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
                                            });
                                            setPostSuccess("Annonce mise à jour avec succès.");
                                        } else {
                                            // Create new post using POST
                                            await axiosClient.post(`${API_CONFIG.BASE_URL}/posts`, payload, {
                                                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...getAuthHeader() }
                                            });
                                            setPostSuccess("Annonce créée avec succès. Elle sera visible après validation.");
                                        }
                                        
                                        // Reset form
                                        setPostForm({
                                            // Payload fields
                                            country_code: 'SN',
                                            title: '',
                                            description: '',
                                            category_id: '',
                                            post_type_id: '',
                                            city_id: '',
                                            contact_name: '',
                                            email: '',
                                            accept_terms: false,
                                            package_id: '',
                                            tags: '',
                                            company_name: '',
                                            company_description: '',
                                            
                                            // Additional UI fields
                                            selected_company_id: '',
                                            startup_date: '',
                                            phone_country: '+221',
                                            phone: '',
                                            hide_phone: false,
                                            application_url: ''
                                        });
                                        setEditingPostId(null);
                                        
                                        // Refresh appropriate list
                                        if (activeTab === 'mes-annonces') {
                                            fetchMyPosts(myPostsPagination.currentPage);
                                        } else if (activeTab === 'en-attente') {
                                            fetchPendingPosts(pendingPagination.currentPage);
                                        } else if (activeTab === 'mes-archives') {
                                            fetchArchivedPosts(archivedPagination.currentPage);
                                        }
                                        
                                        setTimeout(() => setShowPostModal(false), 800);
                                    } catch (e) {
                                        console.error('Erreur lors de la soumission de l\'annonce:', e);
                                        let errorMessage = 'Erreur réseau';
                                        
                                        if (e?.response?.data) {
                                            const errorData = e.response.data;
                                            if (errorData.message) {
                                                errorMessage = errorData.message;
                                            } else if (errorData.errors) {
                                                // Format validation errors
                                                const errorMessages = [];
                                                Object.keys(errorData.errors).forEach(field => {
                                                    if (Array.isArray(errorData.errors[field])) {
                                                        errorMessages.push(...errorData.errors[field]);
                                                    }
                                                });
                                                errorMessage = errorMessages.join(' ');
                                            }
                                        } else if (e?.message) {
                                            errorMessage = e.message;
                                        }
                                        
                                        const action = editingPostId ? 'mettre à jour' : 'créer';
                                        setPostError(`Impossible de ${action} l'annonce: ${errorMessage}`);
                                    } finally {
                                        setPostSubmitting(false);
                                    }
                                }}>
                                    {postSubmitting ? (editingPostId ? 'Mise à jour…' : 'Publication…') : (editingPostId ? 'Mettre à jour' : 'Submit')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Company Modal */}
                {showCompanyModal && (
                    <div className="modal-overlay">
                        <div className="modal-container" style={{ maxWidth: '600px' }}>
                            <div className="modal-header" style={{ borderBottom: '2px solid #f0f0f0', padding: '1.5rem' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#145650', margin: 0 }}>
                                    <i className="fa fa-building" style={{ marginRight: '10px' }}></i>
                                    {editingCompanyId ? 'Modifier une entreprise' : 'Créer une nouvelle entreprise'}
                                </h3>
                                <button className="modal-close" onClick={closeCompanyModal} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <div className="modal-body" style={{ padding: '2rem', maxHeight: '70vh', overflowY: 'auto' }}>
                                {companyCreateError && (
                                    <div className="error-message inline" style={{ marginBottom: '1rem', padding: '12px', background: '#fee2e2', borderRadius: '8px', color: '#dc2626' }}>
                                        <i className="fa fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                                        <span>{companyCreateError}</span>
                                    </div>
                                )}

                                {/* Logo Upload */}
                                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                                    <div 
                                        style={{
                                            width: '120px',
                                            height: '120px',
                                            margin: '0 auto 1rem',
                                            borderRadius: '12px',
                                            border: '3px dashed #145650',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundImage: companyForm.logoFile ? `url(${URL.createObjectURL(companyForm.logoFile)})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                        onClick={() => document.getElementById('companyLogoInput').click()}
                                    >
                                        {!companyForm.logoFile && (
                                            <div style={{ textAlign: 'center', color: '#145650' }}>
                                                <i className="fa fa-cloud-upload-alt" style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }}></i>
                                                <div style={{ fontSize: '12px' }}>Cliquer pour uploader</div>
                                            </div>
                                        )}
                                    </div>
                                    <input 
                                        id="companyLogoInput"
                                        type="file" 
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleCompanyInput('logoFile', e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                    />
                                    <small style={{ color: '#6b7280' }}>Logo de l'entreprise (max 5MB)</small>
                                </div>

                                {/* Informations de base */}
                                <h5 style={{ color: '#145650', marginBottom: '1rem', fontSize: '16px', fontWeight: '600' }}>
                                    <i className="fa fa-info-circle" style={{ marginRight: '8px' }}></i>Informations de base
                                </h5>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Nom de l'entreprise <span style={{ color: '#dc2626' }}>*</span></label>
                                    <input 
                                        type="text" 
                                        value={companyForm.name}
                                        onChange={(e) => handleCompanyInput('name', e.target.value)}
                                        placeholder="Ex: SENELEC" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Description <span style={{ color: '#dc2626' }}>*</span></label>
                                    <textarea 
                                        rows={3}
                                        value={companyForm.description}
                                        onChange={(e) => handleCompanyInput('description', e.target.value)}
                                        placeholder="Description de l'entreprise..."
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', resize: 'vertical' }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Ville</label>
                                    <select 
                                        value={companyForm.city_id}
                                        onChange={(e) => handleCompanyInput('city_id', e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff' }}
                                    >
                                        <option value="">Sélectionner une ville</option>
                                        {cities.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Adresse</label>
                                    <input 
                                        type="text" 
                                        value={companyForm.address}
                                        onChange={(e) => handleCompanyInput('address', e.target.value)}
                                        placeholder="Ex: Rue de la République, Dakar" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>

                                {/* Contact */}
                                <h5 style={{ color: '#145650', marginBottom: '1rem', fontSize: '16px', fontWeight: '600' }}>
                                    <i className="fa fa-phone" style={{ marginRight: '8px' }}></i>Contact
                                </h5>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Téléphone</label>
                                        <input 
                                            type="text" 
                                            value={companyForm.phone}
                                            onChange={(e) => handleCompanyInput('phone', e.target.value)}
                                            placeholder="+221338391000" 
                                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Email</label>
                                        <input 
                                            type="email" 
                                            value={companyForm.email}
                                            onChange={(e) => handleCompanyInput('email', e.target.value)}
                                            placeholder="contact@entreprise.sn" 
                                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>Site web</label>
                                    <input 
                                        type="text" 
                                        value={companyForm.website}
                                        onChange={(e) => handleCompanyInput('website', e.target.value)}
                                        placeholder="https://www.entreprise.sn" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>

                                {/* Réseaux sociaux */}
                                <h5 style={{ color: '#145650', marginBottom: '1rem', fontSize: '16px', fontWeight: '600' }}>
                                    <i className="fa fa-share-alt" style={{ marginRight: '8px' }}></i>Réseaux sociaux
                                </h5>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                                        <i className="fab fa-facebook" style={{ marginRight: '8px', color: '#145650' }}></i>Facebook
                                    </label>
                                    <input 
                                        type="url" 
                                        value={companyForm.facebook}
                                        onChange={(e) => handleCompanyInput('facebook', e.target.value)}
                                        placeholder="https://facebook.com/entreprise" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                                        <i className="fab fa-twitter" style={{ marginRight: '8px', color: '#145650' }}></i>Twitter
                                    </label>
                                    <input 
                                        type="url" 
                                        value={companyForm.twitter}
                                        onChange={(e) => handleCompanyInput('twitter', e.target.value)}
                                        placeholder="https://twitter.com/entreprise" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>

                                <div className="form-group" style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                                        <i className="fab fa-linkedin" style={{ marginRight: '8px', color: '#145650' }}></i>LinkedIn
                                    </label>
                                    <input 
                                        type="url" 
                                        value={companyForm.linkedin}
                                        onChange={(e) => handleCompanyInput('linkedin', e.target.value)}
                                        placeholder="https://linkedin.com/company/entreprise" 
                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer" style={{ borderTop: '2px solid #f0f0f0', padding: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button 
                                    className="btn secondary" 
                                    onClick={closeCompanyModal} 
                                    disabled={companyCreateLoading}
                                    style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}
                                >
                                    Annuler
                                </button>
                                <button 
                                    className="btn primary" 
                                    onClick={editingCompanyId ? updateCompany : createCompany} 
                                    disabled={companyCreateLoading || !companyForm.name || !companyForm.description}
                                    style={{ 
                                        padding: '10px 24px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        background: 'linear-gradient(135deg, #145650 0%, #0d3d39 100%)', 
                                        color: '#fff', 
                                        cursor: 'pointer',
                                        opacity: (!companyForm.name || !companyForm.description) ? 0.6 : 1
                                    }}
                                >
                                    {companyCreateLoading ? (
                                        <>
                                            <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                            {editingCompanyId ? 'Mise à jour...' : 'Création en cours...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-check" style={{ marginRight: '8px' }}></i>
                                            {editingCompanyId ? 'Mettre à jour' : 'Créer l\'entreprise'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EntrepriseDashboard;
